import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, formatMoney, startViewTransition } from '@theme/utilities';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * Preserve color swatch backgrounds from filter buttons before section re-render
 * IMPORTANT: Only preserve backgrounds for FILTER buttons, NOT product card variant picker swatches
 * @returns {Map<string, string>} Map of filter key (param_name:value) to swatch background
 */
const preserveColorSwatchBackgrounds = () => {
  const swatchMap = new Map();
  // ONLY preserve swatch previews in FILTERS (facets), NOT in product cards or variant pickers
  // Look specifically for filter swatches within facets components
  const filterSwatches = document.querySelectorAll('.facets__item .color-swatch__preview, .facets__pill-label--color');
  
  filterSwatches.forEach((swatch) => {
    const preview = swatch;
    
    // CRITICAL: Only preserve filter swatches, NOT product card/variant picker swatches
    // Check if this swatch is inside a product card, variant picker, or product form
    const isInProductCard = preview.closest('.sunice-product-card, .sunice-variant-picker, .product-form-container, .product-form');
    if (isInProductCard) {
      // Skip product card swatches - we don't want to preserve/modify these
      return;
    }
    
    // Only process if this is actually in a facets/filter component
    const isInFacets = preview.closest('.facets__item, .facets__panel, facets-form-component');
    if (!isInFacets) {
      // Not in a filter component, skip it
      return;
    }
    
    // Check both fieldset (for filters) and other parent containers
    // For color filters, we now use radio buttons, so check for both radio and checkbox
    const input = preview.closest('fieldset')?.querySelector('input[type="radio"], input[type="checkbox"]') ||
                  preview.closest('.color-swatch')?.querySelector('input[type="radio"]') ||
                  preview.closest('.color-swatch')?.querySelector('input[type="checkbox"]');
    
    if (input instanceof HTMLInputElement) {
      const paramName = input.name || '';
      const value = input.value || '';
      const key = `${paramName}:${value}`;
      
      // Try to get background from data attribute first, then inline style, then computed style
      let background = preview.dataset.swatchBackground || 
                       preview.style.getPropertyValue('--swatch-background') ||
                       getComputedStyle(preview).getPropertyValue('--swatch-background');
      
      // Clean up the value (remove 'url(' wrapper if present, or just use as-is)
      if (background) {
        background = background.trim();
        // If it's a CSS variable that didn't resolve, try to get the actual value
        if (background.startsWith('var(')) {
          background = preview.dataset.swatchBackground || null;
        }
      }
      
      if (key && background && background !== 'rgb(200, 200, 200)' && background !== '') {
        swatchMap.set(key, background);
      }
    }
  });
  
  return swatchMap;
};

/**
 * Restore color swatch backgrounds to filter buttons after section re-render
 * IMPORTANT: Only restore backgrounds for FILTER buttons, NOT product card variant picker swatches
 * Product card swatches should keep their original backgrounds from the HTML
 * @param {Map<string, string>} swatchMap - Map of filter key (param_name:value) to swatch background
 */
const restoreColorSwatchBackgrounds = (swatchMap) => {
  if (!swatchMap || swatchMap.size === 0) return;
  
  // ONLY restore swatch previews in FILTERS (facets), NOT in product cards or variant pickers
  // Product card swatches should keep their original unique backgrounds
  // Look specifically for filter swatches within facets components
  const filterSwatches = document.querySelectorAll('.facets__item .color-swatch__preview, .facets__pill-label--color');
  
  filterSwatches.forEach((preview) => {
    // CRITICAL: Only process filter swatches, NOT product card/variant picker swatches
    // Check if this swatch is inside a product card, variant picker, or product form
    const isInProductCard = preview.closest('.sunice-product-card, .sunice-variant-picker, .product-form-container, .product-form');
    if (isInProductCard) {
      // Skip product card swatches - they should keep their original unique backgrounds
      // DO NOT modify these swatches - they already have the correct unique backgrounds in the HTML
      return;
    }
    
    // Only process if this is actually in a facets/filter component
    const isInFacets = preview.closest('.facets__item, .facets__panel, facets-form-component');
    if (!isInFacets) {
      // Not in a filter component, skip it
      return;
    }
    
    // Check both fieldset (for filters) and other parent containers
    // For color filters, we now use radio buttons, so check for both radio and checkbox
    const input = preview.closest('fieldset')?.querySelector('input[type="radio"], input[type="checkbox"]') ||
                  preview.closest('.color-swatch')?.querySelector('input[type="radio"]') ||
                  preview.closest('.color-swatch')?.querySelector('input[type="checkbox"]');
    
    if (input instanceof HTMLInputElement) {
      const paramName = input.name || '';
      const value = input.value || '';
      const key = `${paramName}:${value}`;
      const savedBackground = swatchMap.get(key);
      
      if (savedBackground) {
        // Set both data attribute and inline style to ensure it persists
        preview.dataset.swatchBackground = savedBackground;
        preview.style.setProperty('--swatch-background', savedBackground);
        
        // Also ensure the background is applied directly if it's a color (not a URL)
        if (!savedBackground.startsWith('url(')) {
          preview.style.backgroundColor = savedBackground;
        }
      }
    }
  });
  
  // Also restore pill labels that have color swatch backgrounds (these are filter pills)
  const colorPillLabels = document.querySelectorAll('.facets__pill-label--color[data-swatch-background]');
  colorPillLabels.forEach((label) => {
    // Skip if this is in a product card
    const isInProductCard = label.closest('.sunice-product-card, .sunice-variant-picker, .product-form-container, .product-form');
    if (isInProductCard) {
      return;
    }
    
    // Only process if this is actually in a facets/filter component
    const isInFacets = label.closest('.facets__item, .facets__panel, facets-form-component');
    if (!isInFacets) {
      return;
    }
    
    const input = label.previousElementSibling;
    if (input instanceof HTMLInputElement && input.classList.contains('facets__pill-input')) {
      const paramName = input.name || '';
      const value = input.value || '';
      const key = `${paramName}:${value}`;
      const savedBackground = swatchMap.get(key);
      
      if (savedBackground) {
        label.dataset.swatchBackground = savedBackground;
        label.style.setProperty('--swatch-background', savedBackground);
      }
    }
  });
};

/**
 * Extract active filter values from URL parameters
 * Handles both standard Shopify filters and Shopify Search and Discovery filter groups
 * @param {URLSearchParams} urlParams - URL search parameters
 * @returns {Object} Object with filter types and their values
 */
/**
 * Get all colors in a filter group from the DOM
 * Shopify Search and Discovery filter groups can contain multiple colors
 * @param {string} filterColorLabel - The filter group label (e.g., "Red")
 * @returns {string[]} Array of all color values in the group
 */
/**
 * Get all colors in a filter group from Shopify Search and Discovery
 * Filter groups contain all related colors (e.g., "Red" group contains "Scarlet Flame", "Crimson", etc.)
 * @param {string} filterColorLabel - The filter label to find
 * @returns {string[]} Array of all colors in the filter group
 */
/**
 * Get all colors in a filter group from Shopify Search and Discovery
 * Filter groups contain all related colors (e.g., "Red" group contains "Scarlet Flame", "Crimson", etc.)
 * Strategy: Find all filter swatches that share the same merged_value to get the complete group
 * @param {string} filterColorLabel - The filter label to find
 * @returns {string[]} Array of all colors in the filter group
 */
const getFilterGroupColors = (filterColorLabel) => {
  // Find the filter swatch button with this label
  const filterSwatches = document.querySelectorAll('.facets__item .color-swatch[data-color]');
  
  let targetMergedValue = null;
  let targetFilterValue = null;
  
  // First pass: Find the matching swatch and get its merged_value/filter_value
  for (const swatch of filterSwatches) {
    if (!(swatch instanceof HTMLElement)) continue;
    const swatchColor = swatch.dataset.color;
    const swatchTitle = swatch.getAttribute('title') || swatch.textContent?.trim();
    const filterValue = swatch.dataset.filterValue;
    // Try both camelCase and kebab-case for merged value
    const mergedValue = swatch.dataset.mergedValue || swatch.getAttribute('data-merged-value');
    
    // Check if this is the filter we're looking for (match by label, title, or value)
    const normalizedSwatchColor = normalizeString(swatchColor || '');
    const normalizedSwatchTitle = normalizeString(swatchTitle || '');
    const normalizedFilterLabel = normalizeString(filterColorLabel);
    
    if (normalizedSwatchColor === normalizedFilterLabel || 
        normalizedSwatchTitle === normalizedFilterLabel || 
        filterValue === filterColorLabel ||
        normalizedSwatchColor.includes(normalizedFilterLabel) ||
        normalizedFilterLabel.includes(normalizedSwatchColor)) {
      
      targetMergedValue = mergedValue;
      targetFilterValue = filterValue;
      break;
    }
  }
  
  // If we found a merged_value, find ALL swatches with the same merged_value (they're in the same group)
  if (targetMergedValue) {
    const groupColors = /** @type {string[]} */ ([]);
    
    // First, try to parse merged_value to see if it contains color data
    try {
      const parsed = JSON.parse(targetMergedValue);
      
      // Extract colors from parsed structure
      if (typeof parsed === 'string') {
        groupColors.push(parsed);
      } else if (Array.isArray(parsed)) {
        parsed.forEach((/** @type {any} */ item) => {
          if (typeof item === 'string' && !groupColors.includes(item)) {
            groupColors.push(item);
          } else if (item && typeof item === 'object') {
            if (item.label && !groupColors.includes(item.label)) groupColors.push(item.label);
            if (item.value && item.value !== item.label && !groupColors.includes(item.value)) {
              groupColors.push(item.value);
            }
          }
        });
      } else if (typeof parsed === 'object' && parsed !== null) {
        if (parsed.label && !groupColors.includes(parsed.label)) groupColors.push(parsed.label);
        if (parsed.value && !groupColors.includes(parsed.value)) groupColors.push(parsed.value);
        
        // Check for nested color arrays
        Object.values(parsed).forEach((/** @type {any} */ val) => {
          if (typeof val === 'string' && val.trim() && !groupColors.includes(val.trim())) {
            groupColors.push(val.trim());
          } else if (Array.isArray(val)) {
            val.forEach((/** @type {any} */ v) => {
              if (typeof v === 'string' && v.trim() && !groupColors.includes(v.trim())) {
                groupColors.push(v.trim());
              } else if (v && typeof v === 'object' && v.label && !groupColors.includes(v.label)) {
                groupColors.push(v.label);
              }
            });
          }
        });
      }
    } catch (e) {
      console.warn('[FILTER DEBUG] getFilterGroupColors: Error parsing merged_value:', e);
    }
    
    // Collect all colors from swatches that share the same merged_value
    // This is the key: all swatches with the same merged_value are in the same filter group
    for (const swatch of filterSwatches) {
      if (!(swatch instanceof HTMLElement)) continue;
      // Try both camelCase and kebab-case for merged value
      const mergedValue = swatch.dataset.mergedValue || swatch.getAttribute('data-merged-value');
      const swatchColor = swatch.dataset.color;
      const filterValue = swatch.dataset.filterValue;
      const swatchTitle = swatch.getAttribute('title') || swatch.textContent?.trim();
      
      if (mergedValue && mergedValue === targetMergedValue) {
        // This swatch is in the same filter group - collect its color
        if (swatchColor && !groupColors.includes(swatchColor)) {
          groupColors.push(swatchColor);
        }
        if (filterValue && filterValue !== swatchColor && !groupColors.includes(filterValue)) {
          groupColors.push(filterValue);
        }
        if (swatchTitle && swatchTitle !== swatchColor && swatchTitle !== filterValue && !groupColors.includes(swatchTitle)) {
          groupColors.push(swatchTitle);
        }
      }
    }
    
    if (groupColors.length > 0) {
      // Remove duplicates and always include the filter label itself
      const uniqueColors = [...new Set([filterColorLabel, ...groupColors])];
      return uniqueColors;
    }
  }
  
  // If no merged_value but we have filter_value, try to find other swatches with same filter_value
  if (targetFilterValue && !targetMergedValue) {
    const groupColors = [filterColorLabel];
    
    // Also check if the filter_value itself contains multiple colors (e.g., "Red|Scarlet|Crimson")
    if (targetFilterValue.includes('|') || targetFilterValue.includes(',')) {
      const splitColors = targetFilterValue.split(/[|,]/).map(c => c.trim()).filter(c => c);
      if (splitColors.length > 1) {
        return [...new Set([filterColorLabel, ...splitColors])];
      }
    }
    
    for (const swatch of filterSwatches) {
      if (!(swatch instanceof HTMLElement)) continue;
      const filterValue = swatch.dataset.filterValue;
      const swatchColor = swatch.dataset.color;
      const swatchTitle = swatch.getAttribute('title') || swatch.textContent?.trim();
      
      // Check if this swatch has the same filter_value (they're in the same group)
      if (filterValue === targetFilterValue) {
        if (swatchColor && !groupColors.includes(swatchColor)) {
          groupColors.push(swatchColor);
        }
        if (swatchTitle && swatchTitle !== swatchColor && !groupColors.includes(swatchTitle)) {
          groupColors.push(swatchTitle);
        }
      }
    }
    
    if (groupColors.length > 1) {
      return groupColors;
    }
  }
  
  // Try to find colors that are semantically related (e.g., all red shades)
  // This is a fallback when Shopify Search and Discovery doesn't provide group data
  const normalizedLabelForSemantic = normalizeString(filterColorLabel);
  const baseColorForSemantic = getBaseColor(normalizedLabelForSemantic);
  
  if (baseColorForSemantic && baseColorForSemantic !== normalizedLabelForSemantic) {
    // Find all swatches that map to the same base color
    const semanticGroup = [filterColorLabel];
    for (const swatch of filterSwatches) {
      if (!(swatch instanceof HTMLElement)) continue;
      const swatchColor = swatch.dataset.color;
      const swatchTitle = swatch.getAttribute('title') || swatch.textContent?.trim();
      const swatchLabel = swatchColor || swatchTitle;
      
      if (swatchLabel && swatchLabel !== filterColorLabel) {
        const normalizedSwatch = normalizeString(swatchLabel);
        const swatchBaseColor = getBaseColor(normalizedSwatch);
        if (swatchBaseColor === baseColorForSemantic && !semanticGroup.includes(swatchLabel)) {
          semanticGroup.push(swatchLabel);
        }
      }
    }
    
    if (semanticGroup.length > 1) {
      return semanticGroup;
    }
  }
  
  // FALLBACK: If no group data, use synonyms as backup
  const normalizedLabel = normalizeString(filterColorLabel);
  const baseColor = getBaseColor(normalizedLabel);
  
  // If the label maps to a base color, find all synonyms of that base color
  if (baseColor !== normalizedLabel) {
    const synonymColors = [filterColorLabel];
    for (const [synonym, base] of Object.entries(COLOR_SYNONYMS)) {
      if (base === baseColor && synonym !== normalizedLabel) {
        synonymColors.push(synonym);
      }
    }
    if (synonymColors.length > 1) {
      return synonymColors;
    }
  }
  
  // Last resort: just the label
  const result = [filterColorLabel];
  return result;
};

/**
 * Resolve a GID filter value (e.g. gid://shopify/FilterSettingGroup/35881167) to a
 * human-readable label (e.g. "Red") using server-rendered maps and DOM fallbacks.
 */
const resolveColorGid = (raw) => {
  const s = String(raw || '').trim();
  const lower = s.toLowerCase();
  if (!lower || (!lower.includes('gid://') && !lower.includes('filtersettinggroup'))) return s;

  // 1. ColorFilterGidMap (server-rendered JSON)
  const mapEl = document.getElementById('ColorFilterGidMap');
  if (mapEl && mapEl.textContent) {
    try {
      const map = JSON.parse(mapEl.textContent.trim() || '{}');
      for (const [gidKey, label] of Object.entries(map)) {
        if (String(gidKey).toLowerCase() === lower) {
          const resolved = String(label || '').trim();
          if (resolved) return resolved;
        }
      }
    } catch { /* ignore */ }
  }

  // 2. Filter swatches data-filter-value (exact GID match)
  const swatches = document.querySelectorAll('.facets__item .color-swatch[data-filter-value]');
  for (const swatch of swatches) {
    if (!(swatch instanceof HTMLElement)) continue;
    if ((swatch.dataset.filterValue || '').trim().toLowerCase() === lower) {
      const label = swatch.dataset.color || swatch.getAttribute('title') || '';
      if (label) return label;
    }
  }

  // 3. Checkbox values with GID
  const checkboxes = document.querySelectorAll('input[type="checkbox"][value]');
  for (const cb of checkboxes) {
    if (!(cb instanceof HTMLInputElement)) continue;
    if (cb.value.toLowerCase() === lower && cb.name && cb.name.toLowerCase().includes('color')) {
      const li = cb.closest('li, label');
      if (li instanceof HTMLElement) {
        const cs = li.querySelector('.color-swatch');
        if (cs instanceof HTMLElement) {
          const resolved = cs.dataset.color || cs.getAttribute('title') || '';
          if (resolved) return resolved;
        }
      }
    }
  }

  // NOTE: We intentionally do NOT fall back to "any active swatch in the DOM" here.
  // In multi-select scenarios the active-swatch fallbacks caused multiple distinct
  // GIDs to resolve to the same label (the first active swatch), which corrupted
  // the resolved color list and hid valid products. If we can't resolve this
  // specific GID, return the raw string — `_serverFiltered` will be flipped on and
  // the caller will trust the server's result.
  return s;
};

const safeDecodeURIComponent = (value) => {
  if (value == null) return '';
  const s = String(value);
  try {
    return decodeURIComponent(s);
  } catch {
    try {
      return decodeURIComponent(s.replace(/%(?![0-9A-Fa-f]{2})/g, '%25'));
    } catch {
      return s;
    }
  }
};

// Color filter selection-order tracking.
// When the user selects multiple colors (e.g. Red then Blue) we want the FIRST
// selected color to win on each product card. Only if a card doesn't have an
// available variant for that color do we fall back to the next selected color.
// Shopify's URL/FormData order is DOM order, not click order, so we track clicks
// ourselves and persist the order across section re-renders via sessionStorage.
const _SUNICE_COLOR_ORDER_KEY = 'sunice.colorFilterOrder';
/** @type {string[]} */
let _suniceColorSelectionOrder = [];
let _suniceColorOrderLoaded = false;

const _normalizeColorKey = (label) => String(label || '').trim().toLowerCase();

const _loadColorSelectionOrder = () => {
  if (_suniceColorOrderLoaded) return;
  _suniceColorOrderLoaded = true;
  try {
    const raw = sessionStorage.getItem(_SUNICE_COLOR_ORDER_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        _suniceColorSelectionOrder = parsed.filter((v) => typeof v === 'string');
      }
    }
  } catch { /* ignore */ }
};

const _persistColorSelectionOrder = () => {
  try {
    sessionStorage.setItem(_SUNICE_COLOR_ORDER_KEY, JSON.stringify(_suniceColorSelectionOrder));
  } catch { /* ignore */ }
};

const _recordColorSelection = (label, isChecked) => {
  const key = _normalizeColorKey(label);
  if (!key) return;
  _loadColorSelectionOrder();
  const idx = _suniceColorSelectionOrder.findIndex((c) => _normalizeColorKey(c) === key);
  if (isChecked) {
    // Preserve first-selected position — only append if not already present.
    if (idx === -1) _suniceColorSelectionOrder.push(String(label));
  } else if (idx !== -1) {
    _suniceColorSelectionOrder.splice(idx, 1);
  }
  _persistColorSelectionOrder();
};

/**
 * Reorder a colors array so user-preferred (first-selected) colors come first.
 * Colors not in the tracked selection order keep their original relative order
 * and are appended after the prioritized ones.
 * @param {string[]} colors
 * @returns {string[]}
 */
const _orderColorsBySelection = (colors) => {
  if (!Array.isArray(colors) || colors.length <= 1) return Array.isArray(colors) ? colors.slice() : [];
  _loadColorSelectionOrder();
  if (_suniceColorSelectionOrder.length === 0) return colors.slice();
  const seen = new Set();
  const ordered = [];
  for (const pref of _suniceColorSelectionOrder) {
    const pk = _normalizeColorKey(pref);
    for (const c of colors) {
      const ck = _normalizeColorKey(c);
      if (ck === pk && !seen.has(ck)) {
        ordered.push(c);
        seen.add(ck);
      }
    }
  }
  for (const c of colors) {
    const ck = _normalizeColorKey(c);
    if (!seen.has(ck)) {
      ordered.push(c);
      seen.add(ck);
    }
  }
  return ordered;
};

// Prune selections that are no longer present in the URL (e.g. after "Clear all"
// or unchecking via a different UI). Called from extractActiveFilters so the
// tracked order stays in sync with what's actually active.
const _reconcileColorSelectionOrder = (activeLabels) => {
  _loadColorSelectionOrder();
  if (_suniceColorSelectionOrder.length === 0) return;
  const activeKeys = new Set(activeLabels.map(_normalizeColorKey));
  const before = _suniceColorSelectionOrder.length;
  _suniceColorSelectionOrder = _suniceColorSelectionOrder.filter((c) => activeKeys.has(_normalizeColorKey(c)));
  // Also append any active color not yet tracked (e.g. arrived via direct URL
  // navigation) so they still participate in priority resolution.
  for (const label of activeLabels) {
    if (!_suniceColorSelectionOrder.some((c) => _normalizeColorKey(c) === _normalizeColorKey(label))) {
      _suniceColorSelectionOrder.push(label);
    }
  }
  if (_suniceColorSelectionOrder.length !== before) _persistColorSelectionOrder();
  else _persistColorSelectionOrder();
};

// Capture facet color input changes BEFORE the form submits so we can record
// the click order. Capture phase ensures we run before Shopify's handlers
// trigger a section re-render.
document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (target.type !== 'checkbox' && target.type !== 'radio') return;
  if (!target.closest('.facets__item, facets-form-component, facet-inputs-component')) return;
  const name = (target.name || '').toLowerCase();
  if (!name.includes('color') && !name.includes('colour')) return;

  let label = '';
  const swatch = target.closest('.color-swatch');
  if (swatch instanceof HTMLElement) {
    label = swatch.dataset.color || swatch.getAttribute('title') || '';
  }
  if (!label) {
    const parent = target.closest('li, label');
    if (parent instanceof HTMLElement) {
      const sw = parent.querySelector('.color-swatch');
      if (sw instanceof HTMLElement) label = sw.dataset.color || sw.getAttribute('title') || '';
      if (!label) label = parent.getAttribute('data-color') || parent.getAttribute('title') || '';
    }
  }
  if (!label && target.value) {
    const resolved = resolveColorGid(target.value);
    if (resolved && !resolved.includes('gid://') && !resolved.toLowerCase().includes('filtersettinggroup')) {
      label = resolved;
    }
  }
  if (label) _recordColorSelection(label, target.checked);
}, true);

const extractActiveFilters = (urlParams) => {
  const filters = {
    colors: [],
    variants: [],
    other: {},
    colorGroups: {}
  };

  for (const [key, value] of urlParams.entries()) {
    if (key.startsWith('filter.')) {
      const filterKey = key.toLowerCase();
      if (filterKey.includes('color') || filterKey.includes('colour')) {
        const decodedColor = resolveColorGid(safeDecodeURIComponent(value));
        // If the GID couldn't be resolved, the server already filtered correctly — skip
        // client-side filtering to avoid hiding all products with an unmatchable GID string.
        if (decodedColor.includes('gid://') || decodedColor.toLowerCase().includes('filtersettinggroup')) {
          filters._serverFiltered = true;
          continue;
        }
        filters.colors.push(decodedColor);
        
        // Get all colors in this filter group (if it's a group)
        const groupColors = getFilterGroupColors(decodedColor);
        if (groupColors.length > 1 || groupColors[0] !== decodedColor) {
          filters.colorGroups[decodedColor] = groupColors;
        }
      } else if (filterKey.includes('variant')) {
        filters.variants.push(safeDecodeURIComponent(value));
      } else {
        if (!filters.other[key]) {
          filters.other[key] = [];
        }
        filters.other[key].push(safeDecodeURIComponent(value));
      }
    }
  }

  // Also check for filter_group params (Search & Discovery group format, NOT standard filter.v.option.color)
  for (const [key, value] of urlParams.entries()) {
    const lowerKey = key.toLowerCase();
    if (lowerKey.includes('filter') && lowerKey.includes('group') && !lowerKey.startsWith('filter.v.option.') && !lowerKey.startsWith('filter.p.m.')) {
      const decodedValue = safeDecodeURIComponent(value);
      try {
        if (decodedValue.startsWith('{') || decodedValue.startsWith('[')) {
          const parsed = JSON.parse(decodedValue);
          if (Array.isArray(parsed)) {
            parsed.forEach(item => {
              if (typeof item === 'string') filters.colors.push(item);
            });
          } else {
            filters.colors.push(decodedValue);
          }
        } else {
          filters.colors.push(decodedValue);
        }
      } catch {
        filters.colors.push(decodedValue);
      }
    }
  }

  // Keep tracked selection order in sync with what's actually active, then
  // reorder filters.colors so the first-selected color has priority downstream
  // (e.g. in updateProductCardForFilter, which picks the first matching color).
  _reconcileColorSelectionOrder(filters.colors);
  filters.colors = _orderColorsBySelection(filters.colors);

  return filters;
};

/**
 * Normalize a string for comparison (lowercase, trim, handle special characters)
 * @param {string} str - String to normalize
 * @returns {string} Normalized string
 */
const normalizeString = (str) => {
  if (!str) return '';
  // Convert to string, lowercase, trim, normalize whitespace
  let normalized = String(str).toLowerCase().trim();
  // Normalize whitespace (multiple spaces to single space)
  normalized = normalized.replace(/\s+/g, ' ');
  // Handle forward slashes (both escaped and unescaped)
  normalized = normalized.replace(/\\\//g, '/');
  // Remove any trailing/leading slashes
  normalized = normalized.replace(/^\/+|\/+$/g, '');
  return normalized;
};

/**
 * Default color synonyms (fallback if Shopify metafield is not configured)
 * Maps color shades to their base colors
 */
const DEFAULT_COLOR_SYNONYMS = {
  // Red shades
  'scarlet': 'red',
  'crimson': 'red',
  'burgundy': 'red',
  'maroon': 'red',
  'cherry': 'red',
  'rose': 'red',
  'coral': 'red',
  'salmon': 'red',
  'pink': 'red',
  'magenta': 'red',
  'ruby': 'red',
  'vermillion': 'red',
  'cardinal': 'red',
  'carmine': 'red',
  
  // Blue shades
  'navy': 'blue',
  'azure': 'blue',
  'cyan': 'blue',
  'teal': 'blue',
  'turquoise': 'blue',
  'sapphire': 'blue',
  'cobalt': 'blue',
  'indigo': 'blue',
  'royal': 'blue',
  'sky': 'blue',
  
  // Green shades
  'emerald': 'green',
  'lime': 'green',
  'olive': 'green',
  'mint': 'green',
  'jade': 'green',
  'forest': 'green',
  'sage': 'green',
  'fern': 'green',
  
  // Yellow shades
  'gold': 'yellow',
  'amber': 'yellow',
  'lemon': 'yellow',
  'mustard': 'yellow',
  'canary': 'yellow',
  
  // Other colors
  'ivory': 'white',
  'cream': 'white',
  'beige': 'white',
  'tan': 'brown',
  'khaki': 'brown',
  'chocolate': 'brown',
  'coffee': 'brown',
  // Charcoal is a distinct facet (and dark grey) — do not map to "black" or
  // Charcoal filters will incorrectly match "Black" variants, and "Grey" will
  // miss products that are only named Charcoal.
  'charcoal': 'gray',
  'slate': 'gray',
  'silver': 'gray',
  'platinum': 'gray',
  'grey': 'gray',
};

/**
 * @param {Record<string, any> | null | undefined} [activeFilters]
 * @param {string} filterKey
 * @returns {string[]}
 */
const getColorGroupListForKey = (activeFilters, filterKey) => {
  if (!filterKey) return [filterKey];
  const g = activeFilters && activeFilters.colorGroups && activeFilters.colorGroups[filterKey];
  if (Array.isArray(g) && g.length > 0) {
    return g;
  }
  // URL parse often omits the group when it only has one value; the facet DOM
  // still has merged_value with the full Search & Discovery group
  const fromDom = getFilterGroupColors(filterKey);
  if (Array.isArray(fromDom) && fromDom.length > 0) {
    return fromDom;
  }
  return [filterKey];
};

/**
 * Pick the best product colour string for the PLP when several variants match
 * a filter group (e.g. prefer a literal "Black" over "Charcoal" for the Black
 * swatch, and a primary "Magnesium" / "X/Magnesium" where Magnesium is the
 * leading name over "Blue Stone/Magnesium").
 * @param {string} variantColor
 * @param {string} filterColorLabel
 * @param {string[]} [filterGroupColors]
 */
const scoreVariantForFilterColor = (variantColor, filterColorLabel, filterGroupColors) => {
  if (!variantColor || !filterColorLabel) return 0;
  const nVar = normalizeString(String(variantColor));
  const nF = normalizeString(String(filterColorLabel));
  if (!nVar || !nF) return 0;
  if (nVar === nF) return 1_000_000;

  const nGroup = Array.isArray(filterGroupColors)
    ? filterGroupColors.map((c) => normalizeString(c)).filter(Boolean)
    : [];

  for (const g of nGroup) {
    if (g && nVar === g) {
      if (g === nF) return 950_000;
      return 900_000;
    }
  }

  const segs = nVar.split('/').map((s) => s.trim()).filter(Boolean);
  if (segs[0] === nF) return 890_000;
  for (let i = 0; i < segs.length; i++) {
    if (segs[i] === nF) {
      // Prefer the filter as the *main* / first part of the name (e.g. Magnesium over
      // "Blue Stone/Magnesium" where Magnesium is the accent)
      return 870_000 - i * 5_000;
    }
  }
  for (const seg of segs) {
    for (const word of nF.split(/\s+/)) {
      if (word.length > 1 && seg === word) return 820_000;
    }
  }
  if (nF.length > 1) {
    const at = nVar.indexOf(nF);
    if (at === 0) return 600_000;
    if (at > 0) return 300_000;
  }
  return 1000;
};

/**
 * Color synonym/alias map for semantic matching
 * Maps color shades to their base colors
 * Loaded from Shopify shop metafield (custom.color_synonyms)
 * Falls back to DEFAULT_COLOR_SYNONYMS if not set
 */
let COLOR_SYNONYMS = { ...DEFAULT_COLOR_SYNONYMS };

/**
 * Load color synonyms from Shopify shop metafield
 * This function should be called on page load
 */
const loadColorSynonyms = () => {
  try {
    const colorSynonymsScript = document.getElementById('color-synonyms-data');
    if (colorSynonymsScript instanceof HTMLScriptElement) {
      const synonymsData = JSON.parse(colorSynonymsScript.textContent || '{}');
      if (synonymsData && typeof synonymsData === 'object' && Object.keys(synonymsData).length > 0) {
        // Merge Shopify data with defaults (Shopify data takes precedence)
        COLOR_SYNONYMS = { ...DEFAULT_COLOR_SYNONYMS, ...synonymsData };
      } else {
        // Use defaults if Shopify metafield is empty or not set
        COLOR_SYNONYMS = { ...DEFAULT_COLOR_SYNONYMS };
      }
    } else {
      // Script not found, use defaults
      COLOR_SYNONYMS = { ...DEFAULT_COLOR_SYNONYMS };
    }
  } catch (e) {
    console.warn('[FILTER DEBUG] Failed to load color synonyms from Shopify, using defaults:', e);
    COLOR_SYNONYMS = { ...DEFAULT_COLOR_SYNONYMS };
  }
  if (COLOR_SYNONYMS.charcoal === 'black') {
    COLOR_SYNONYMS = { ...COLOR_SYNONYMS, charcoal: 'gray' };
  }
};

// Load color synonyms on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', loadColorSynonyms);
} else {
  loadColorSynonyms();
}

/**
 * Get base color from a color name (handles synonyms)
 * @param {string} colorName - The color name to check
 * @returns {string} The base color name
 */
const getBaseColor = (colorName) => {
  const normalized = normalizeString(colorName);
  const words = normalized.split(/[\s\/\\\-_]+/);
  
  // Check each word for synonyms
  for (const word of words) {
    if (COLOR_SYNONYMS[word]) {
      return COLOR_SYNONYMS[word];
    }
  }
  
  return normalized;
};

/**
 * Single-filter semantic match (synonyms, base colour, substrings) — used for
 * facet groups where the list omits a variant name (e.g. "Crimson") but the
 * list still contains the group label ("Red").
 * @param {string} colorValue
 * @param {string} filterString - One filter or group label
 */
const semanticColorMatch = (colorValue, filterString) => {
  if (!colorValue || !filterString) return false;
  const normalizedColor = normalizeString(colorValue);
  const normalizedFilter = normalizeString(filterString);
  if (!normalizedColor || !normalizedFilter) return false;
  const baseFilterColor = getBaseColor(normalizedFilter);
  const baseProductColor = getBaseColor(normalizedColor);
  if (baseFilterColor === baseProductColor) {
    return true;
  }
  const productWords = normalizedColor.split(/[\s\/\\\-_]+/);
  for (const word of productWords) {
    const wordBase = getBaseColor(word);
    if (wordBase === baseFilterColor) {
      return true;
    }
  }
  if (normalizedColor.includes(normalizedFilter) || normalizedFilter.includes(normalizedColor)) {
    return true;
  }
  return false;
};

/**
 * Check if a color value matches any of the active color filters
 * Uses Shopify Search and Discovery filter groups when available, falls back to semantic matching
 * @param {string} colorValue - Color value to check (variant title/color)
 * @param {string[]} activeColors - Array of active color filter values from the filter group (from Shopify Search and Discovery)
 * @returns {boolean} True if the variant title/color exists in the filter group
 */
const colorMatchesFilter = (colorValue, activeColors) => {
  if (!activeColors || activeColors.length === 0) {
    return false;
  }
  if (!colorValue) {
    return false;
  }
  
  const normalizedColor = normalizeString(colorValue);
  
  // Check if the variant title/color matches ANY color in the filter group
  // Shopify Search and Discovery already provides all related colors in the group
  const directMatch = activeColors.some(activeColor => {
    const normalizedActive = normalizeString(activeColor);
    
    // Try exact match first (most strict)
    if (normalizedColor === normalizedActive) {
      return true;
    }
    
    // Try partial matches - check if the variant color contains the filter value or vice versa
    // This handles cases like "Blue" matching "Dark Blue/Pure White" or "Blue Stone/Midnight"
    if (normalizedColor.includes(normalizedActive) || normalizedActive.includes(normalizedColor)) {
      const words = normalizedColor.split(/[\s\/\\\-_]+/);
      const activeWords = normalizedActive.split(/\s+/);
      
      // If filter has multiple words, check if variant contains all words
      if (activeWords.length > 1) {
        const matches = activeWords.every(activeWord => 
          words.some(word => word.includes(activeWord) || activeWord.includes(word))
        );
        if (matches) {
        }
        return matches;
      }
      
      // For single-word filters, check if any word in the product color matches
      const productWords = normalizedColor.split(/[\s\/\\\-_]+/);
      const hasWordMatch = productWords.some(word => {
        const exactMatch = word === normalizedActive;
        const contains = word.includes(normalizedActive) || normalizedActive.includes(word);
        if (exactMatch || contains) {
          return true;
        }
        return false;
      });
      
      if (hasWordMatch) {
        return true;
      }
      
      return true;
    }
    
    // Try removing special characters for comparison (handles "Blue/White" vs "Blue White")
    const colorClean = normalizedColor.replace(/[\/\\\-_]/g, ' ').replace(/\s+/g, ' ').trim();
    const activeClean = normalizedActive.replace(/[\/\\\-_]/g, ' ').replace(/\s+/g, ' ').trim();
    if (colorClean === activeClean) {
      return true;
    }
    
    // Check if words match (handles "Dark Blue" vs "Blue Dark")
    const colorWords = colorClean.split(/\s+/).sort().join(' ');
    const activeWordsSorted = activeClean.split(/\s+/).sort().join(' ');
    if (colorWords === activeWordsSorted) {
      return true;
    }
    
    return false;
  });
  
  if (directMatch) {
    return true;
  }

  // Semantic / synonym match: essential when a multi-value S&D group in the DOM
  // lists facet labels (e.g. "Red", "Burgundy") but not every variant name
  // (e.g. "Crimson") — the strict direct matcher would fail for those variants.
  // - Single label: same behaviour as before.
  // - Full group: try the same rules against each list entry (O(n) labels; n is small).
  for (const filterLabel of activeColors) {
    if (typeof filterLabel === 'string' && semanticColorMatch(colorValue, filterLabel)) {
      return true;
    }
  }
  return false;
};

/**
 * Find and select the exact variant that matches the active filters
 * This function finds the variant from the product's variant data and selects both color and size
 * @param {HTMLElement} container - Container element (product card or product form)
 * @param {Object} activeFilters - Active filter values
 * @returns {boolean} True if variant was selected
 */
const selectVariantFromFilter = (container, activeFilters) => {
  if (!(container instanceof HTMLElement) || !activeFilters || activeFilters.colors.length === 0) {
    return false;
  }

  const picker = container.querySelector('.sunice-variant-picker');
  if (!(picker instanceof HTMLElement)) return false;
  
  // Get variant data
  const variantsDataScript = picker.querySelector('.product-variants-data');
  if (!(variantsDataScript instanceof HTMLScriptElement)) return false;
  
  let variantData = null;
  try {
    variantData = JSON.parse(variantsDataScript.textContent || '{}');
  } catch (e) {
    return false;
  }
  
  if (!variantData.variants || !Array.isArray(variantData.variants)) return false;
  
  // Get filter group colors (all colors in the filter group, not just the label)
  const filterColor = activeFilters.colors[0];
  const filterGroupColors = activeFilters.colorGroups?.[filterColor] || [filterColor];
  
  // First, collect all variants that match ANY color in the filter group
  const matchingColorVariants = [];
  for (const variant of variantData.variants) {
    if (!variant.options || variant.options.length < 1) continue;
    
    const variantColor = String(variant.options[0] || '').trim();
    
    // Check if variant color matches ANY color in the filter group using the same matching logic
    if (colorMatchesFilter(variantColor, filterGroupColors)) {
      matchingColorVariants.push(variant);
    }
  }
  
  
  if (matchingColorVariants.length === 0) return false;

  matchingColorVariants.sort((a, b) => {
    const aCol = String(a.options[0] || '');
    const bCol = String(b.options[0] || '');
    return (
      scoreVariantForFilterColor(bCol, filterColor, filterGroupColors) -
      scoreVariantForFilterColor(aCol, filterColor, filterGroupColors)
    );
  });

  // After ranking, prefer an in-stock variant; otherwise show the best-ranked
  // match so merchandising (Black vs Charcoal) stays consistent
  let matchingVariant = null;
  for (const variant of matchingColorVariants) {
    if (variant.available === true) {
      matchingVariant = variant;
      break;
    }
  }
  if (!matchingVariant && matchingColorVariants.length > 0) {
    matchingVariant = matchingColorVariants[0];
  }

  if (!matchingVariant) return false;

  // Now select the color and size that match this variant
  const variantColor = String(matchingVariant.options[0] || '').trim();
  const variantSize = matchingVariant.options.length > 1 ? String(matchingVariant.options[1] || '').trim() : null;
  const variantId = String(matchingVariant.id || '');

  const updated = updateVariantPickerForColor(
    container,
    variantColor,
    variantSize,
    { label: filterColor, group: filterGroupColors }
  );
  
  // CRITICAL: Also update the hidden variant ID input to match the selected variant
  // Do this after a small delay to ensure the variant picker has been updated
  if (updated && variantId) {
    setTimeout(() => {
      // Find the form (could be in product-form, product-form-container, or product card)
      const form = container.closest('form') || 
                   container.querySelector('form') ||
                   container.querySelector('.product-form') ||
                   container.querySelector('.product-form-container');
      
      if (form instanceof HTMLFormElement) {
        const variantIdInput = form.querySelector('input[name="id"]');
        if (variantIdInput instanceof HTMLInputElement) {
          variantIdInput.value = variantId;
          
          // Trigger change event to ensure other handlers are notified
          const changeEvent = new Event('change', { bubbles: true });
          variantIdInput.dispatchEvent(changeEvent);
        } else {
          console.warn('[FILTER DEBUG] selectVariantFromFilter: Variant ID input not found');
        }
      } else {
        console.warn('[FILTER DEBUG] selectVariantFromFilter: Form not found');
      }
    }, 150);
  }
  
  return updated;
};

/**
 * Update variant picker inside product form or product card to select matching color and size
 * @param {HTMLElement} container - Container element (product card or product form)
 * @param {string} colorValue - Color value to match
 * @param {string} [sizeValue] - Optional size value to match (if not provided, will find first available)
 * @param {{ label?: string, group?: string[] } | null} [filterContext] - For PLP colour filters, pass the
 *   selected filter label and S&D group so we can pick the best match when several radios are tied.
 * @returns {boolean} True if variant picker was updated
 */
const updateVariantPickerForColor = (container, colorValue, sizeValue = null, filterContext = null) => {
  if (!(container instanceof HTMLElement) || !colorValue) return false;

  const picker = container.querySelector('.sunice-variant-picker');
  if (!(picker instanceof HTMLElement)) return false;
  
  // Check if picker is initialized (has data-initialized attribute or has fieldsets)
  // If not initialized yet, we might need to wait, but try anyway
  const hasFieldsets = picker.querySelectorAll('fieldset').length > 0;
  if (!hasFieldsets) {
    // Picker might not be initialized yet, but we'll still try
    // This helps catch cases where the picker exists but isn't fully rendered
  }
  
  // Get variant data to find the correct variant
  const variantsDataScript = picker.querySelector('.product-variants-data');
  let variantData = null;
  if (variantsDataScript instanceof HTMLScriptElement) {
    try {
      variantData = JSON.parse(variantsDataScript.textContent || '{}');
    } catch (e) {
      // Ignore parsing errors
    }
  }

  // Find the Color or Colour option fieldset
  const colorFieldsets = Array.from(picker.querySelectorAll('fieldset'));
  for (const fieldset of colorFieldsets) {
    if (!(fieldset instanceof HTMLFieldSetElement)) continue;
    const legend = fieldset.querySelector('legend');
    const optionName = legend?.textContent?.trim();
    
    // Check for "Color", "Colour", or "Core:" (which is used for color in some templates)
    const isColorFieldset = optionName && (
      optionName.toLowerCase() === 'color' || 
      optionName.toLowerCase() === 'colour' ||
      optionName.toLowerCase().replace(':', '').trim() === 'core'
    );
    
    // Store the color option name for later use
    const colorOptionName = optionName || 'Color';
    
    if (isColorFieldset) {
      const labelForScore = (filterContext && filterContext.label) || colorValue;
      const groupForScore =
        filterContext && filterContext.group && filterContext.group.length
          ? filterContext.group
          : [labelForScore];

      const isRadioColorMatch = (/** @type {HTMLInputElement} */ radio) => {
        const normalizedRadioValue = normalizeString(radio.value);
        const normalizedColorValue = normalizeString(colorValue);
        let isMatch = normalizedRadioValue === normalizedColorValue;
        if (!isMatch) {
          const radioClean = normalizedRadioValue.replace(/[\/\\]/g, '').replace(/\s+/g, '');
          const colorClean = normalizedColorValue.replace(/[\/\\]/g, '').replace(/\s+/g, '');
          isMatch = radioClean === colorClean;
        }
        if (!isMatch) {
          if (normalizedRadioValue.includes(normalizedColorValue) || normalizedColorValue.includes(normalizedRadioValue)) {
            isMatch = true;
          } else {
            const colorWords = normalizedColorValue.split(/\s+/);
            const radioWords = normalizedRadioValue.split(/\s+/);
            for (const colorWord of colorWords) {
              if (colorWord.length > 2 && radioWords.some((rw) => rw.includes(colorWord) || colorWord.includes(rw))) {
                isMatch = true;
                break;
              }
            }
          }
        }
        if (!isMatch) {
          isMatch = colorMatchesFilter(radio.value, groupForScore);
        }
        return isMatch;
      };

      const radioButtons = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      const candidates = radioButtons.filter(
        (r) => r instanceof HTMLInputElement && isRadioColorMatch(r)
      );
      if (candidates.length === 0) {
        continue;
      }
      candidates.sort((a, b) => {
        if (!(a instanceof HTMLInputElement) || !(b instanceof HTMLInputElement)) {
          return 0;
        }
        return (
          scoreVariantForFilterColor(b.value, labelForScore, groupForScore) -
          scoreVariantForFilterColor(a.value, labelForScore, groupForScore)
        );
      });
      const radio = /** @type {HTMLInputElement} */ (candidates[0]);
      {
          // Uncheck all other radios in this fieldset first
          radioButtons.forEach((r) => {
            if (r instanceof HTMLInputElement && r !== radio) {
              r.checked = false;
              // Also update label states
              const rLabel = r.closest('label');
              if (rLabel instanceof HTMLElement) {
                rLabel.classList.remove('active');
                rLabel.removeAttribute('data-active');
              }
            }
          });
          
          // Check the radio button
          radio.checked = true;
          
          // Update label active state
          const label = radio.closest('label');
          if (label instanceof HTMLElement) {
            label.classList.add('active');
            if (label.classList.contains('color-swatch')) {
              label.setAttribute('data-active', 'true');
            }
          }
          
          // Get image URLs from the selected radio button's data attributes
          // These should be the correct images for this specific color variant
          // Each color should have its own unique image URLs - CRITICAL: Use the radio's data attributes
          let imageUrl = radio.dataset.imageUrl || '';
          let secondaryImageUrl = radio.dataset.secondaryImageUrl || '';
          
          // If the radio doesn't have image URLs, try to get them from the label
          if (!imageUrl) {
            const label = radio.closest('label');
            if (label instanceof HTMLElement) {
              imageUrl = label.dataset.imageUrl || '';
              secondaryImageUrl = label.dataset.secondaryImageUrl || '';
            }
          }
          
          // CRITICAL: Each color radio has unique data-image-url - we MUST use it
          // Don't fall back to other sources if we have the radio's image URL
          // The radio's data-image-url is the correct unique image for this specific color
          
          // Update product images - check both product cards and product forms
          const productCard = container.closest('.sunice-product-card');
          const productForm = container.closest('.product-form, .product-form-container');
          const globalWindow = /** @type {any} */ (window);
          
          // Update product card images if this is inside a product card
          if (productCard instanceof HTMLElement && imageUrl) {
            if (typeof globalWindow.updateProductCardPrimaryImage === 'function') {
              // Use the unique image URLs from this specific color radio button
              globalWindow.updateProductCardPrimaryImage(productCard, imageUrl, secondaryImageUrl);
            } else {
              // Fallback: manually update images
              const primaryImage = productCard.querySelector('.product-image-primary');
              const secondaryImage = productCard.querySelector('.product-image-secondary');
              if (primaryImage instanceof HTMLImageElement && imageUrl) {
                primaryImage.src = imageUrl;
              }
              if (secondaryImage instanceof HTMLImageElement && secondaryImageUrl) {
                secondaryImage.src = secondaryImageUrl;
              }
            }
          }
          
          // Also update images in product forms (for product pages)
          // Look for media gallery or product images
          if (productForm instanceof HTMLElement && imageUrl) {
            // Try to find and update product images in the form context
            const formImages = productForm.querySelectorAll('img[src*="/products/"], img[src*="/files/"], .product-image-primary, .product-image-secondary');
            formImages.forEach((img) => {
              if (img instanceof HTMLImageElement) {
                // Check if this is a primary or secondary image
                if (img.classList.contains('product-image-primary') || 
                    img.src.includes('model_a') || 
                    img.src.includes('_a.')) {
                  if (imageUrl && img.src !== imageUrl) {
                    img.src = imageUrl;
                  }
                } else if (img.classList.contains('product-image-secondary') || 
                          img.src.includes('model_c') || 
                          img.src.includes('_c.')) {
                  if (secondaryImageUrl && img.src !== secondaryImageUrl) {
                    img.src = secondaryImageUrl;
                  }
                }
              }
            });
            
            // Also try to update via media gallery if it exists
            const mediaGallery = productForm.closest('body')?.querySelector('media-gallery, .media-gallery');
            if (mediaGallery && typeof globalWindow.updateProductCardPrimaryImage === 'function') {
              // Try to find product card in the page context
              const pageProductCard = document.querySelector('.sunice-product-card[data-product-id="' + picker.dataset.productId + '"]');
              if (pageProductCard instanceof HTMLElement) {
                globalWindow.updateProductCardPrimaryImage(pageProductCard, imageUrl, secondaryImageUrl);
              }
            }
          }
          
          // After selecting color, find and select the correct size FOR THIS SPECIFIC COLOR
          // If sizeValue is provided, try to match that specific size
          // Otherwise, find the first available size for the selected color
          // CRITICAL: We need to find a size that has an available variant for the selected color
          const sizeFieldsets = Array.from(picker.querySelectorAll('fieldset'));
          for (const sizeFieldset of sizeFieldsets) {
            if (!(sizeFieldset instanceof HTMLFieldSetElement)) continue;
            const sizeLegend = sizeFieldset.querySelector('legend');
            const sizeOptionName = sizeLegend?.textContent?.trim();
            
            // Check if this is a size fieldset (not color)
            const isSizeFieldset = sizeOptionName && 
              sizeOptionName.toLowerCase() !== 'color' && 
              sizeOptionName.toLowerCase() !== 'colour' &&
              sizeOptionName.toLowerCase().replace(':', '').trim() !== 'core';
            
            if (isSizeFieldset) {
              // Get the selected color value to match against variant data
              const selectedColorValue = radio.value;
              
              const sizeRadios = Array.from(sizeFieldset.querySelectorAll('input[type="radio"]'));
              let targetSizeRadio = null;
              
              // If a specific size was requested, try to find it first
              if (sizeValue && variantData && variantData.variants) {
                const normalizedRequestedSize = normalizeString(sizeValue);
                const normalizedSelectedColor = normalizeString(selectedColorValue);
                
                // First, check if the requested size has an available variant for the selected color
                const matchingVariantForSize = variantData.variants.find((v) => {
                  if (!v.options || v.options.length < 2) return false;
                  const variantColor = normalizeString(String(v.options[0] || ''));
                  const variantSize = normalizeString(String(v.options[1] || ''));
                  
                  return variantColor === normalizedSelectedColor &&
                         variantSize === normalizedRequestedSize &&
                         v.available === true;
                });
                
                // If we found an available variant for the requested size, use it
                if (matchingVariantForSize) {
                  for (const sizeRadio of sizeRadios) {
                    if (!(sizeRadio instanceof HTMLInputElement)) continue;
                    const sizeRadioValue = normalizeString(sizeRadio.value);
                    
                    if (sizeRadioValue === normalizedRequestedSize && 
                        !sizeRadio.disabled && 
                        !sizeRadio.closest('label')?.classList.contains('unavailable')) {
                      targetSizeRadio = sizeRadio;
                      break;
                    }
                  }
                }
              }
              
              // If no specific size was requested or the requested size isn't available, find first available
              if (!targetSizeRadio && variantData && variantData.variants) {
                for (const sizeRadio of sizeRadios) {
                  if (!(sizeRadio instanceof HTMLInputElement)) continue;
                  
                  const sizeValue = sizeRadio.value;
                  
                  // Find a variant that matches both the selected color and this size, and is available
                  const matchingVariant = variantData.variants.find((v) => {
                    if (!v.options || v.options.length < 2) return false;
                    // Check if color matches (first option)
                    const variantColor = String(v.options[0] || '').trim();
                    const normalizedVariantColor = normalizeString(variantColor);
                    const normalizedSelectedColor = normalizeString(selectedColorValue);
                    
                    // Check if size matches (second option)
                    const variantSize = String(v.options[1] || '').trim();
                    const normalizedVariantSize = normalizeString(variantSize);
                    const normalizedSizeValue = normalizeString(sizeValue);
                    
                    return normalizedVariantColor === normalizedSelectedColor &&
                           normalizedVariantSize === normalizedSizeValue &&
                           v.available === true;
                  });
                  
                  if (matchingVariant) {
                    // Check if the size radio is not disabled
                    const isNotDisabled = !sizeRadio.disabled && 
                                          !sizeRadio.closest('label')?.classList.contains('unavailable');
                    if (isNotDisabled) {
                      targetSizeRadio = sizeRadio;
                      break;
                    }
                  }
                }
              }
              
              // If still no size found using variant data, try data attributes
              if (!targetSizeRadio) {
                for (const sizeRadio of sizeRadios) {
                  if (!(sizeRadio instanceof HTMLInputElement)) continue;
                  
                  // Check if this size radio has the correct variant ID for the selected color
                  const variantId = sizeRadio.dataset.variantId;
                  let isAvailable = false;
                  
                  if (variantId && variantData && variantData.variants) {
                    const variant = variantData.variants.find((v) => String(v.id) === String(variantId));
                    if (variant && variant.available && variant.options && variant.options.length > 0) {
                      const variantColor = String(variant.options[0] || '').trim();
                      const normalizedVariantColor = normalizeString(variantColor);
                      const normalizedSelectedColor = normalizeString(selectedColorValue);
                      if (normalizedVariantColor === normalizedSelectedColor) {
                        isAvailable = true;
                      }
                    }
                  } else {
                    // Fallback: check data attributes
                    isAvailable = sizeRadio.dataset.optionAvailable !== 'false' && 
                                !sizeRadio.disabled &&
                                !sizeRadio.closest('label')?.classList.contains('unavailable');
                  }
                  
                  if (isAvailable) {
                    targetSizeRadio = sizeRadio;
                    break;
                  }
                }
              }
              
              // If still no available size found for this color, try to find any available size
              // (This handles edge cases where the color might not have any available sizes)
              if (!targetSizeRadio) {
                for (const sizeRadio of sizeRadios) {
                  if (!(sizeRadio instanceof HTMLInputElement)) continue;
                  const isAvailable = sizeRadio.dataset.optionAvailable !== 'false' && 
                                    !sizeRadio.disabled &&
                                    !sizeRadio.closest('label')?.classList.contains('unavailable');
                  
                  if (isAvailable) {
                    targetSizeRadio = sizeRadio;
                    break;
                  }
                }
              }
              
              // Select the target size
              if (targetSizeRadio) {
                // Uncheck all size radios first
                sizeRadios.forEach((r) => {
                  if (r instanceof HTMLInputElement && r !== targetSizeRadio) {
                    r.checked = false;
                    const rLabel = r.closest('label');
                    if (rLabel instanceof HTMLElement) {
                      rLabel.classList.remove('active');
                      rLabel.removeAttribute('data-active');
                    }
                  }
                });
                
                // Check the target size
                targetSizeRadio.checked = true;
                const sizeLabel = targetSizeRadio.closest('label');
                if (sizeLabel instanceof HTMLElement) {
                  sizeLabel.classList.add('active');
                  sizeLabel.setAttribute('data-active', 'true');
                }
                
                // Trigger change event for the size radio to ensure other handlers are called
                const sizeChangeEvent = new Event('change', { bubbles: true });
                targetSizeRadio.dispatchEvent(sizeChangeEvent);
                
                // Also call handleVariantChange if available
                if (typeof globalWindow.handleVariantChange === 'function') {
                  globalWindow.handleVariantChange(targetSizeRadio, picker);
                }
              }
              break;
            }
          }
          
          // Update the selected swatch background on product-form-container
          // This ensures the --selected-swatch-background CSS variable matches the selected color
          if (typeof globalWindow.updateProductCardSwatchBackground === 'function') {
            const selectedOptions = {};
            selectedOptions[colorOptionName || 'Color'] = radio.value;
            
            // Also get the selected size if available
            const selectedSizeRadio = picker.querySelector('input[type="radio"][name*="Size"]:checked');
            if (selectedSizeRadio instanceof HTMLInputElement) {
              const sizeFieldset = selectedSizeRadio.closest('fieldset');
              if (sizeFieldset instanceof HTMLFieldSetElement) {
                const sizeLegend = sizeFieldset.querySelector('legend');
                const sizeOptionName = sizeLegend?.textContent?.trim();
                if (sizeOptionName) {
                  selectedOptions[sizeOptionName] = selectedSizeRadio.value;
                }
              }
            }
            
            globalWindow.updateProductCardSwatchBackground(picker, selectedOptions);
          }
          
          // CRITICAL: Update product card images when color is selected
          // This ensures images update when clicking color swatches in the variant picker
          if (imageUrl && productCard instanceof HTMLElement) {
            if (typeof globalWindow.updateProductCardPrimaryImage === 'function') {
              globalWindow.updateProductCardPrimaryImage(productCard, imageUrl, secondaryImageUrl);
            }
          }
          
          // Also update color swiper swatches in the product card to match the selected color
          if (productCard instanceof HTMLElement) {
            const colorSwiperContainer = productCard.querySelector('.color-swiper-container');
            if (colorSwiperContainer instanceof HTMLElement) {
              const colorValue = radio.value;
              const colorSwatches = colorSwiperContainer.querySelectorAll('.color-swatch');
              
              // Find and activate the matching color swatch in the color swiper
              for (const swatch of colorSwatches) {
                if (!(swatch instanceof HTMLElement)) continue;
                const swatchColor = swatch.dataset.color;
                if (swatchColor) {
                  const normalizedSwatchColor = normalizeString(swatchColor);
                  const normalizedRadioColor = normalizeString(colorValue);
                  
                  // Check if colors match
                  if (normalizedSwatchColor === normalizedRadioColor ||
                      normalizedSwatchColor.includes(normalizedRadioColor) ||
                      normalizedRadioColor.includes(normalizedSwatchColor)) {
                    // Update active state
                    colorSwatches.forEach((s) => {
                      if (s instanceof HTMLElement) {
                        s.removeAttribute('data-active');
                      }
                    });
                    swatch.setAttribute('data-active', 'true');
                    break;
                  }
                }
              }
            }
          }
          
          // Trigger variant change handler if available
          
          // Try to trigger change event first (for proper event handling)
          const changeEvent = new Event('change', { bubbles: true });
          radio.dispatchEvent(changeEvent);
          
          // Also call handleVariantChange if available (for product cards)
          // This will handle variant ID updates, availability updates, etc.
          if (typeof globalWindow.handleVariantChange === 'function') {
            globalWindow.handleVariantChange(radio, picker);
          }
          
          // Update the hidden variant ID input to match the selected variant
          // CRITICAL: Do this after a small delay to ensure both color and size are selected
          setTimeout(() => {
            const form = container.closest('form');
            if (form instanceof HTMLFormElement) {
              const variantIdInput = form.querySelector('input[name="id"]');
              if (variantIdInput instanceof HTMLInputElement) {
                // Find the variant ID from the selected color and size
                const selectedColorRadio = picker.querySelector('input[type="radio"][name*="Color"]:checked');
                const selectedSizeRadio = picker.querySelector('input[type="radio"][name*="Size"]:checked');
                
                if (selectedColorRadio && selectedSizeRadio) {
                  // Get variant data to find the correct variant ID
                  const variantsDataScript = picker.querySelector('.product-variants-data');
                  let variantId = null;
                  
                  if (variantsDataScript instanceof HTMLScriptElement) {
                    try {
                      const variantData = JSON.parse(variantsDataScript.textContent || '{}');
                      if (variantData.variants) {
                        // Find the variant that matches both the selected color and size
                        const selectedColorValue = selectedColorRadio.value;
                        const selectedSizeValue = selectedSizeRadio.value;
                        
                        // First, try to find an available variant
                        let matchingVariant = variantData.variants.find((v) => {
                          if (!v.options || v.options.length < 2) return false;
                          // Normalize for comparison
                          const variantColor = normalizeString(String(v.options[0] || ''));
                          const variantSize = normalizeString(String(v.options[1] || ''));
                          const normalizedSelectedColor = normalizeString(selectedColorValue);
                          const normalizedSelectedSize = normalizeString(selectedSizeValue);
                          
                          return variantColor === normalizedSelectedColor &&
                                 variantSize === normalizedSelectedSize &&
                                 v.available === true;
                        });
                        
                        // If no available variant found, find any matching variant
                        if (!matchingVariant) {
                          matchingVariant = variantData.variants.find((v) => {
                            if (!v.options || v.options.length < 2) return false;
                            const variantColor = normalizeString(String(v.options[0] || ''));
                            const variantSize = normalizeString(String(v.options[1] || ''));
                            const normalizedSelectedColor = normalizeString(selectedColorValue);
                            const normalizedSelectedSize = normalizeString(selectedSizeValue);
                            
                            return variantColor === normalizedSelectedColor &&
                                   variantSize === normalizedSelectedSize;
                          });
                        }
                        
                        if (matchingVariant) {
                          variantId = String(matchingVariant.id);
                        }
                      }
                    } catch (e) {
                      // Ignore parsing errors
                    }
                  }
                  
                  // If we couldn't find from variant data, try the size radio's variant ID
                  if (!variantId) {
                    variantId = selectedSizeRadio.dataset.variantId;
                  }
                  
                  // If still no variant ID, try the color radio's variant ID (fallback)
                  if (!variantId) {
                    variantId = selectedColorRadio.dataset.variantId;
                  }
                  
                  if (variantId) {
                    variantIdInput.value = variantId;
                    updateProductCardLinksForVariant(container, variantId);
                  }
                } else if (selectedColorRadio && selectedColorRadio.dataset.variantId) {
                  // Fallback to color radio's variant ID
                  variantIdInput.value = selectedColorRadio.dataset.variantId;
                  updateProductCardLinksForVariant(container, selectedColorRadio.dataset.variantId);
                }
              }
            }
          }, 100);
          
          return true;
      }
      break;
    }
  }
  
  return false;
};

/**
 * Does this product card have at least ONE in-stock variant that satisfies
 * every active facet (color + any `filter.v.option.*` like size)?
 * Returns true when we can't tell (missing variant data) so we don't over-hide.
 * @param {HTMLElement} productCard
 * @param {Object} activeFilters
 * @returns {boolean}
 */
const productHasAvailableMatchingVariant = (productCard, activeFilters) => {
  const picker = productCard.querySelector('.sunice-variant-picker');
  const script = picker?.querySelector('.product-variants-data');
  if (!(script instanceof HTMLScriptElement)) return true;
  let data;
  try { data = JSON.parse(script.textContent || '{}'); }
  catch { return true; }
  if (!data || !Array.isArray(data.variants)) return true;

  // Build: optionName(lower) -> index in variant.options[]
  const nameToPos = new Map();
  if (Array.isArray(data.options)) {
    data.options.forEach((o) => {
      if (o && typeof o.name === 'string' && typeof o.position === 'number') {
        nameToPos.set(o.name.toLowerCase().trim(), o.position - 1);
      }
    });
  }
  // Color option index (falls back to 0).
  let colorPos = 0;
  for (const name of ['color', 'colour']) {
    if (nameToPos.has(name)) { colorPos = nameToPos.get(name); break; }
  }

  // Collect per-option constraints from active filter.v.option.* values.
  /** @type {{pos:number, values:string[]}[]} */
  const optionConstraints = [];
  const other = (activeFilters && activeFilters.other) || {};
  for (const [key, values] of Object.entries(other)) {
    const lower = key.toLowerCase();
    if (!lower.startsWith('filter.v.option.')) continue;
    const optName = lower.substring('filter.v.option.'.length);
    if (optName === 'color' || optName === 'colour') continue;
    if (!Array.isArray(values) || values.length === 0) continue;
    const pos = nameToPos.get(optName);
    if (pos === undefined) continue; // product doesn't have this option — skip constraint
    optionConstraints.push({ pos, values: values.map((v) => normalizeString(v)) });
  }

  const colors = Array.isArray(activeFilters.colors) ? activeFilters.colors : [];

  // Match each filter color individually — colorMatchesFilter only applies
  // semantic/synonym fallback (so "Sage" matches "Green", etc.) when the
  // active-colors array has length 1. Passing the whole array at once would
  // disable that fallback and over-hide cards in multi-color selections.
  const variantMatchesAnyColor = (variantColor) => {
    if (!variantColor) return false;
    for (const c of colors) {
      if (colorMatchesFilter(variantColor, getColorGroupListForKey(activeFilters, c))) {
        return true;
      }
    }
    return false;
  };

  for (const v of data.variants) {
    if (!v || v.available !== true || !Array.isArray(v.options)) continue;
    if (colors.length > 0) {
      const variantColor = String(v.options[colorPos] || '').trim();
      if (!variantMatchesAnyColor(variantColor)) continue;
    }
    let passesOptions = true;
    for (const { pos, values } of optionConstraints) {
      const vVal = normalizeString(v.options[pos] || '');
      if (!values.includes(vVal)) { passesOptions = false; break; }
    }
    if (passesOptions) return true;
  }
  return false;
};

/**
 * Update product card to show the variant matching the active filter.
 * @param {HTMLElement} productCard - Product card element
 * @param {Object} activeFilters - Active filter values
 * @returns {boolean} true if a matching available swatch was found (or no swiper exists)
 */
const updateProductCardForFilter = (productCard, activeFilters) => {
  if (!(productCard instanceof HTMLElement)) return true;
  if (activeFilters.colors.length === 0) return true;

  // Hard gate: if the product has no in-stock variant matching ALL active
  // facets (color + size etc.), hide the card. This is the "variant for the
  // colour selection isn't available" rule.
  if (!productHasAvailableMatchingVariant(productCard, activeFilters)) {
    return false;
  }

  // Find color swiper container
  const colorSwiperContainer = productCard.querySelector('.color-swiper-container');
  if (!colorSwiperContainer) {
    // No swiper — drive the variant picker using the highest-priority filter
    // color that has a matching radio. Also check the product's variant JSON:
    // if we can tell NO available variant matches any selected color, report
    // unavailable so the card gets hidden.
    let hasAvailableMatch = null;
    const picker = productCard.querySelector('.sunice-variant-picker');
    const variantsDataScript = picker?.querySelector('.product-variants-data');
    if (variantsDataScript instanceof HTMLScriptElement) {
      try {
        const data = JSON.parse(variantsDataScript.textContent || '{}');
        if (data && Array.isArray(data.variants)) {
          hasAvailableMatch = data.variants.some((v) => {
            if (!v || v.available !== true || !Array.isArray(v.options)) return false;
            const variantColor = String(v.options[0] || '').trim();
            if (!variantColor) return false;
            // Check one color at a time so colorMatchesFilter's semantic
            // synonym fallback ("Sage" -> "Green") applies in multi-select.
            return activeFilters.colors.some((c) => {
              const g = getColorGroupListForKey(activeFilters, c);
              return colorMatchesFilter(variantColor, g);
            });
          });
        }
      } catch { /* ignore */ }
    }
    for (const filterColor of activeFilters.colors) {
      const g = getColorGroupListForKey(activeFilters, filterColor);
      if (updateVariantPickerForColor(
        productCard,
        filterColor,
        null,
        { label: filterColor, group: g }
      )) {
        break;
      }
    }
    return hasAvailableMatch === false ? false : true;
  }

  // Find all color swatches. The swiper renders swatches for BOTH available and
  // out-of-stock variants (distinguished by data-option-available). We only treat
  // AVAILABLE swatches as matches so OOS cards are hidden.
  const colorSwatches = colorSwiperContainer.querySelectorAll('.color-swatch');
  let matchingSwatch = null;

  const isSwatchAvailable = (swatch) => {
    if (!(swatch instanceof HTMLElement)) return false;
    const attr = (swatch.getAttribute('data-option-available') || '').toLowerCase();
    // Default to available if attribute is missing/unclear; only explicit "false" means OOS.
    return attr !== 'false';
  };

  // Iterate filter colours in selection order. Within each product, prefer the
  // best-fitting available swatch (e.g. literal "Black" over "Charcoal", and
  // primary "Magnesium" / "X/Magnesium" over a compound where Magnesium is
  // only the accent) using Search & Discovery group membership + scoring.
  let matchingFilterKey = null;
  for (const filterColor of activeFilters.colors) {
    const groupList = getColorGroupListForKey(activeFilters, filterColor);
    let best = null;
    let bestSc = -1;
    for (const swatch of colorSwatches) {
      if (!(swatch instanceof HTMLElement)) continue;
      const swatchColor = swatch.dataset.color;
      if (!swatchColor) continue;
      if (!colorMatchesFilter(swatchColor, groupList)) continue;
      if (!isSwatchAvailable(swatch)) continue;
      const sc = scoreVariantForFilterColor(swatchColor, filterColor, groupList);
      if (sc > bestSc) {
        bestSc = sc;
        best = swatch;
      }
    }
    if (best) {
      matchingSwatch = best;
      matchingFilterKey = filterColor;
      break;
    }
  }

  // If we found a matching swatch, update the product card
  if (matchingSwatch) {
    // Update active state in swiper - remove active from all others first
    const activeSwatches = colorSwiperContainer.querySelectorAll('.color-swatch[data-active="true"]');
    activeSwatches.forEach((activeSwatch) => {
      if (activeSwatch !== matchingSwatch) {
        activeSwatch.removeAttribute('data-active');
      }
    });
    matchingSwatch.setAttribute('data-active', 'true');

    // Get the correct image URLs from the matching swatch
    // These should be unique to this specific color variant
    // CRITICAL: Each color swatch must have its own unique image URLs
    let imageUrl = matchingSwatch.dataset.imageUrl || '';
    let secondaryImageUrl = matchingSwatch.dataset.secondaryImageUrl || '';
    
    // If swatch doesn't have image URLs, try to get from button element inside swatch
    if (!imageUrl && matchingSwatch instanceof HTMLElement) {
      const swatchButton = matchingSwatch.querySelector('button');
      if (swatchButton instanceof HTMLElement) {
        imageUrl = swatchButton.dataset.imageUrl || '';
        secondaryImageUrl = swatchButton.dataset.secondaryImageUrl || '';
      }
    }
    
    // CRITICAL: If we still don't have image URLs, get them from the variant picker
    // The variant picker radio buttons have the correct unique image URLs for each color
    if (!imageUrl) {
      const picker = productCard.querySelector('.sunice-variant-picker');
      if (picker instanceof HTMLElement) {
        const colorValue = matchingSwatch.dataset.color;
        if (colorValue) {
          // Find the radio button for this color in the variant picker
          const colorRadios = picker.querySelectorAll('input[type="radio"][name*="Color"]');
          for (const colorRadio of colorRadios) {
            if (!(colorRadio instanceof HTMLInputElement)) continue;
            const normalizedRadioValue = normalizeString(colorRadio.value);
            const normalizedColorValue = normalizeString(colorValue);
            if (normalizedRadioValue === normalizedColorValue) {
              // Found the matching color radio - use its unique image URLs
              imageUrl = colorRadio.dataset.imageUrl || '';
              secondaryImageUrl = colorRadio.dataset.secondaryImageUrl || '';
              break;
            }
          }
        }
      }
    }
    
    // If secondary image is missing or same as primary, derive from product image ordering
    if (imageUrl && (!secondaryImageUrl || normalizeString(secondaryImageUrl) === normalizeString(imageUrl))) {
      try {
        const raw = productCard.dataset.productImages;
        if (raw) {
          const ordered = JSON.parse(raw);
          if (Array.isArray(ordered) && ordered.length > 1) {
            const primaryNorm = normalizeString(imageUrl);
            const idx = ordered.findIndex((u) => normalizeString(u) === primaryNorm);
            if (idx >= 0 && idx < ordered.length - 1) {
              secondaryImageUrl = ordered[idx + 1];
            } else if (idx === ordered.length - 1) {
              secondaryImageUrl = ordered[0];
            }
          }
        }
      } catch { /* ignore */ }
    }

    if (imageUrl) {
      const globalWindow = /** @type {any} */ (window);
      if (typeof globalWindow.updateProductCardPrimaryImage === 'function') {
        globalWindow.updateProductCardPrimaryImage(productCard, imageUrl, secondaryImageUrl);
      } else {
        const primaryImage = productCard.querySelector('.product-image-primary');
        const secondaryImage = productCard.querySelector('.product-image-secondary');
        if (primaryImage instanceof HTMLImageElement && imageUrl) {
          primaryImage.src = imageUrl;
        }
        if (secondaryImage instanceof HTMLImageElement && secondaryImageUrl) {
          secondaryImage.src = secondaryImageUrl;
        }
      }
    }

    // Update variant picker and variant title
    const colorValue = matchingSwatch.dataset.color;
    if (colorValue && matchingFilterKey) {
      const g = getColorGroupListForKey(activeFilters, matchingFilterKey);
      updateVariantPickerForColor(
        productCard,
        colorValue,
        null,
        { label: matchingFilterKey, group: g }
      );
      const variantTitle = productCard.querySelector('.variant-title');
      if (variantTitle instanceof HTMLElement) {
        variantTitle.textContent = colorValue;
      }
    }
    return true; // Found a matching available swatch
  } else {
    // No matching swatch in swiper — the filter color is not available on this product.
    // Still try to update the variant picker (for image swap), but report unavailable.
    for (const filterColor of activeFilters.colors) {
      const g = getColorGroupListForKey(activeFilters, filterColor);
      if (updateVariantPickerForColor(
        productCard,
        filterColor,
        null,
        { label: filterColor, group: g }
      )) {
        break;
      }
    }
    return false;
  }
};

/**
 * Swap filter images AND hide cards whose matching color variant is out of stock.
 * Re-usable across page load, back-navigation, pagination, and delayed re-apply.
 * @param {Object} filters - Filter object with at least a `colors` array
 */
/**
 * Update the visible product count in the products-count-wrapper.
 * @param {number} count
 */
const updateProductCount = (count) => {
  document.querySelectorAll('.products-count-wrapper span').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    const txt = (el.textContent || '').trim();
    if (/\d/.test(txt)) {
      el.textContent = txt.replace(/\d[\d,]*/, String(count));
    } else {
      el.textContent = `${count} items`;
    }
  });
};

const applyFilterImagesToCards = (filters) => {
  if (!filters || filters.colors.length === 0) return;
  document.querySelectorAll('.sunice-product-card').forEach((card) => {
    if (!(card instanceof HTMLElement)) return;
    const isAvailable = updateProductCardForFilter(card, filters);
    const gridItem = card.closest('.product-grid__item, li');
    if (!(gridItem instanceof HTMLElement)) return;
    if (!isAvailable) {
      // No available variant matches the selected color(s) — hide the card.
      gridItem.classList.add('filter-hidden');
      gridItem.style.display = 'none';
    } else if (gridItem.classList.contains('filter-hidden')) {
      gridItem.classList.remove('filter-hidden');
      if (gridItem.style.display === 'none') gridItem.style.display = '';
    }
  });
  // Recount visible items (covers both regular and variant listing modes)
  const allVisible = document.querySelectorAll(
    '.product-grid__item:not([style*="display: none"]):not(.filter-hidden):not(.product-grid__item--hidden)'
  );
  updateProductCount(allVisible.length);
};

/**
 * Apply filter selections to all product cards and product forms
 * @param {Object} activeFilters - Active filter values
 */
/**
 * Reset variant picker to default state (first available color)
 * @param {HTMLElement} container - Container element (product card or product form)
 */
const resetVariantPickerToDefault = (container) => {
  if (!(container instanceof HTMLElement)) return;
  
  const picker = container.querySelector('.sunice-variant-picker');
  if (!(picker instanceof HTMLElement)) return;
  
  // Get variant data
  const variantsDataScript = picker.querySelector('.product-variants-data');
  if (!(variantsDataScript instanceof HTMLScriptElement)) return;
  
  let variantData = null;
  try {
    variantData = JSON.parse(variantsDataScript.textContent || '{}');
  } catch (e) {
    return;
  }
  
  if (!variantData.variants || !Array.isArray(variantData.variants)) return;
  
  // Find the Color or Colour option fieldset
  const colorFieldsets = Array.from(picker.querySelectorAll('fieldset'));
  for (const fieldset of colorFieldsets) {
    if (!(fieldset instanceof HTMLFieldSetElement)) continue;
    const legend = fieldset.querySelector('legend');
    const optionName = legend?.textContent?.trim();
    
    // Check for "Color", "Colour", or "Core:" (which is used for color in some templates)
    const isColorFieldset = optionName && (
      optionName.toLowerCase() === 'color' || 
      optionName.toLowerCase() === 'colour' ||
      optionName.toLowerCase().replace(':', '').trim() === 'core'
    );
    
    if (isColorFieldset) {
      // Find the first available color variant
      const radioButtons = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      let firstAvailableRadio = null;
      
      // First, try to find the first available color
      for (const radio of radioButtons) {
        if (!(radio instanceof HTMLInputElement)) continue;
        const isAvailable = radio.dataset.optionAvailable !== 'false' && 
                          !radio.disabled &&
                          !radio.closest('label')?.classList.contains('unavailable');
        
        if (isAvailable) {
          firstAvailableRadio = radio;
          break;
        }
      }
      
      // If no available color found, use the first one
      if (!firstAvailableRadio && radioButtons.length > 0) {
        firstAvailableRadio = radioButtons[0];
      }
      
      if (firstAvailableRadio instanceof HTMLInputElement) {
        // Uncheck all other radios
        radioButtons.forEach((r) => {
          if (r instanceof HTMLInputElement && r !== firstAvailableRadio) {
            r.checked = false;
            const rLabel = r.closest('label');
            if (rLabel instanceof HTMLElement) {
              rLabel.classList.remove('active');
              rLabel.removeAttribute('data-active');
            }
          }
        });
        
        // Check the first available radio
        firstAvailableRadio.checked = true;
        const label = firstAvailableRadio.closest('label');
        if (label instanceof HTMLElement) {
          label.classList.add('active');
          if (label.classList.contains('color-swatch')) {
            label.setAttribute('data-active', 'true');
          }
        }
        
        // Get image URLs from the selected radio
        let imageUrl = firstAvailableRadio.dataset.imageUrl || '';
        let secondaryImageUrl = firstAvailableRadio.dataset.secondaryImageUrl || '';
        
        if (!imageUrl) {
          const label = firstAvailableRadio.closest('label');
          if (label instanceof HTMLElement) {
            imageUrl = label.dataset.imageUrl || '';
            secondaryImageUrl = label.dataset.secondaryImageUrl || secondaryImageUrl;
          }
        }
        
        // Update product card images
        const productCard = container.closest('.sunice-product-card');
        if (productCard instanceof HTMLElement && (imageUrl || secondaryImageUrl)) {
          const globalWindow = /** @type {any} */ (window);
          if (typeof globalWindow.updateProductCardPrimaryImage === 'function') {
            globalWindow.updateProductCardPrimaryImage(productCard, imageUrl, secondaryImageUrl);
          }
        }
        
        // Update color swiper to match - reset to first available color
        if (productCard instanceof HTMLElement) {
          const colorSwiperContainer = productCard.querySelector('.color-swiper-container');
          if (colorSwiperContainer instanceof HTMLElement) {
            const colorValue = firstAvailableRadio.value;
            const colorSwatches = colorSwiperContainer.querySelectorAll('.color-swatch');
            
            // Find and activate the matching color swatch (first available)
            let foundMatchingSwatch = false;
            for (const swatch of colorSwatches) {
              if (!(swatch instanceof HTMLElement)) continue;
              const swatchColor = swatch.dataset.color;
              const isAvailable = swatch.dataset.optionAvailable !== 'false';
              
              if (swatchColor) {
                const normalizedSwatchColor = normalizeString(swatchColor);
                const normalizedRadioColor = normalizeString(colorValue);
                
                // Check if colors match AND it's available (prefer available swatches)
                if ((normalizedSwatchColor === normalizedRadioColor ||
                    normalizedSwatchColor.includes(normalizedRadioColor) ||
                    normalizedRadioColor.includes(normalizedSwatchColor)) && isAvailable) {
                  // Remove active from all swatches first
                  colorSwatches.forEach((s) => {
                    if (s instanceof HTMLElement) {
                      s.removeAttribute('data-active');
                    }
                  });
                  swatch.setAttribute('data-active', 'true');
                  foundMatchingSwatch = true;
                  break;
                }
              }
            }
            
            // If no matching available swatch found, find the first available swatch
            if (!foundMatchingSwatch) {
              for (const swatch of colorSwatches) {
                if (!(swatch instanceof HTMLElement)) continue;
                const isAvailable = swatch.dataset.optionAvailable !== 'false';
                if (isAvailable) {
                  colorSwatches.forEach((s) => {
                    if (s instanceof HTMLElement) {
                      s.removeAttribute('data-active');
                    }
                  });
                  swatch.setAttribute('data-active', 'true');
                  break;
                }
              }
            }
          }
        }
        
        // Find and select the first available size for this color
        const sizeFieldsets = Array.from(picker.querySelectorAll('fieldset'));
        for (const sizeFieldset of sizeFieldsets) {
          if (!(sizeFieldset instanceof HTMLFieldSetElement)) continue;
          const sizeLegend = sizeFieldset.querySelector('legend');
          const sizeOptionName = sizeLegend?.textContent?.trim();
          
          const isSizeFieldset = sizeOptionName && 
            sizeOptionName.toLowerCase() !== 'color' && 
            sizeOptionName.toLowerCase() !== 'colour' &&
            sizeOptionName.toLowerCase().replace(':', '').trim() !== 'core';
          
          if (isSizeFieldset && variantData && variantData.variants) {
            const selectedColorValue = firstAvailableRadio.value;
            const sizeRadios = Array.from(sizeFieldset.querySelectorAll('input[type="radio"]'));
            
            // Find first available size for the selected color
            for (const sizeRadio of sizeRadios) {
              if (!(sizeRadio instanceof HTMLInputElement)) continue;
              const currentSizeValue = sizeRadio.value;
              
              const matchingVariant = variantData.variants.find((v) => {
                if (!v.options || v.options.length < 2) return false;
                const normalizedVariantColor = normalizeString(String(v.options[0] || ''));
                const normalizedVariantSize = normalizeString(String(v.options[1] || ''));
                const normalizedSelectedColor = normalizeString(selectedColorValue);
                const normalizedCurrentSize = normalizeString(currentSizeValue);
                
                return normalizedVariantColor === normalizedSelectedColor &&
                       normalizedVariantSize === normalizedCurrentSize &&
                       v.available === true;
              });
              
              if (matchingVariant && !sizeRadio.disabled && 
                  !sizeRadio.closest('label')?.classList.contains('unavailable')) {
                // Uncheck all size radios
                sizeRadios.forEach((r) => {
                  if (r instanceof HTMLInputElement && r !== sizeRadio) {
                    r.checked = false;
                    const rLabel = r.closest('label');
                    if (rLabel instanceof HTMLElement) {
                      rLabel.classList.remove('active');
                      rLabel.removeAttribute('data-active');
                    }
                  }
                });
                
                // Check the first available size
                sizeRadio.checked = true;
                const sizeLabel = sizeRadio.closest('label');
                if (sizeLabel instanceof HTMLElement) {
                  sizeLabel.classList.add('active');
                  sizeLabel.setAttribute('data-active', 'true');
                }
                
                // Update variant ID input
                const form = container.closest('form');
                if (form instanceof HTMLFormElement) {
                  const variantIdInput = form.querySelector('input[name="id"]');
                  if (variantIdInput instanceof HTMLInputElement) {
                    variantIdInput.value = String(matchingVariant.id);
                  }
                }
                
                break;
              }
            }
            break;
          }
        }
        
        // Trigger change event
        const changeEvent = new Event('change', { bubbles: true });
        firstAvailableRadio.dispatchEvent(changeEvent);
        
        // Call handleVariantChange if available
        const globalWindow = /** @type {any} */ (window);
        if (typeof globalWindow.handleVariantChange === 'function') {
          globalWindow.handleVariantChange(firstAvailableRadio, picker);
        }
        
        // Update variant title if it exists
        if (productCard instanceof HTMLElement) {
          const variantTitle = productCard.querySelector('.variant-title');
          if (variantTitle instanceof HTMLElement) {
            variantTitle.textContent = firstAvailableRadio.value;
          }
        }
        
        // Update swatch background CSS variable
        if (typeof globalWindow.updateProductCardSwatchBackground === 'function') {
          const selectedOptions = {};
          const colorOptionName = optionName || 'Color';
          selectedOptions[colorOptionName] = firstAvailableRadio.value;
          
          // Also get the selected size if available
          const selectedSizeRadio = picker.querySelector('input[type="radio"][name*="Size"]:checked');
          if (selectedSizeRadio instanceof HTMLInputElement) {
            const sizeFieldset = selectedSizeRadio.closest('fieldset');
            if (sizeFieldset instanceof HTMLFieldSetElement) {
              const sizeLegend = sizeFieldset.querySelector('legend');
              const sizeOptionName = sizeLegend?.textContent?.trim();
              if (sizeOptionName) {
                selectedOptions[sizeOptionName] = selectedSizeRadio.value;
              }
            }
          }
          
          globalWindow.updateProductCardSwatchBackground(picker, selectedOptions);
        }
      }
      break;
    }
  }
};

/**
 * Hide color filter options that have ZERO available products
 * Uses Shopify Search and Discovery API counts (data-filter-count) instead of manual counting
 * Show out-of-stock state for filters that have products but all variants are unavailable
 * IMPORTANT: Only hide filters with 0 products in the ENTIRE collection, not based on active filters
 */
const hideUnavailableColorFilters = () => {
  // DISABLED: Never hide any filter options - all filters should remain visible at all times
  // Find all color filter swatches/buttons and ensure they're all visible
  const colorFilterSwatches = document.querySelectorAll('.facets__item .color-swatch[data-color]');
  if (colorFilterSwatches.length === 0) return;
  
  // Ensure ALL filter options are visible - never hide any
  colorFilterSwatches.forEach((filterSwatch) => {
    if (!(filterSwatch instanceof HTMLElement)) return;
    
    const listItem = filterSwatch.closest('li.facets__inputs-list-item');
    const targetElement = listItem instanceof HTMLElement ? listItem : filterSwatch;
    
    // Always show all filters - never hide any
    targetElement.style.display = '';
    targetElement.removeAttribute('data-no-products');
    targetElement.classList.remove('out-of-stock');
  });
};

// Track if filters are currently being applied to prevent multiple simultaneous applications
let isApplyingFilters = false;

/**
 * Normalize color string for filtering comparison
 */
const normalizeColorForFiltering = (str) => {
  if (!str) return '';
  return str.toLowerCase().trim().replace(/\s+/g, ' ');
};

/**
 * Keep product card links pointed at the selected variant on collection pages.
 * @param {HTMLElement} container
 * @param {string | number} variantId
 */
const updateProductCardLinksForVariant = (container, variantId) => {
  if (!(container instanceof HTMLElement) || !variantId) return;

  const productCard = container.matches('.sunice-product-card, .sunice-variant-product-card')
    ? container
    : container.closest('.sunice-product-card, .sunice-variant-product-card');
  if (!(productCard instanceof HTMLElement)) return;

  const firstProductLink = productCard.querySelector('a[href*="/products/"]');
  const picker = productCard.querySelector('.sunice-variant-picker');
  const baseUrl = picker?.getAttribute('data-product-url') || firstProductLink?.getAttribute('href')?.split('?')[0] || '';
  if (!baseUrl) return;

  let nextUrl = '';
  try {
    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('variant', String(variantId));
    nextUrl = url.toString();
  } catch (error) {
    return;
  }

  productCard.querySelectorAll('a[href*="/products/"]').forEach((link) => {
    if (link instanceof HTMLAnchorElement) {
      link.href = nextUrl;
    }
  });

  const galleryLink = productCard.querySelector('.product-card__gallery-link');
  if (galleryLink instanceof HTMLAnchorElement) {
    galleryLink.href = nextUrl;
  }
};

/**
 * Get metafield color from a variant card (top-level utility)
 * @param {Element | HTMLElement} card - The card element
 * @returns {string} The metafield color value or empty string
 */
const getMetafieldColorFromCard = (card) => {
  if (!(card instanceof HTMLElement)) return '';
  const fromLi = card.dataset.metafieldColor || '';
  if (fromLi) return fromLi;
  const inner = card.querySelector('.sunice-variant-product-card, .sunice-product-card');
  if (inner instanceof HTMLElement) {
    return inner.getAttribute('data-metafield-color-filter') || inner.getAttribute('data-metafield-color') || '';
  }
  return '';
};

/**
 * Get color groups map from DOM
 */
const getColorGroupsMap = () => {
  const script = document.getElementById('ColorGroupsJSON');
  if (script instanceof HTMLScriptElement) {
    try {
      const parsed = JSON.parse(script.textContent || '{}');
      return parsed;
    } catch (e) {
      return {};
    }
  }
  return {};
};

/**
 * Expand selected colors for variant filtering
 * Combines activeFilters.colorGroups with ColorGroupsJSON mapping
 */
const expandSelectedColorsForVariantFiltering = (selectedColors, activeFilters, groupsMap) => {
  const expanded = new Set();
  
  selectedColors.forEach(color => {
    const normalized = normalizeColorForFiltering(color);
    expanded.add(normalized);
    
    // First check activeFilters.colorGroups (from Search & Discovery)
    const searchDiscoveryGroup = activeFilters?.colorGroups?.[color];
    if (searchDiscoveryGroup && Array.isArray(searchDiscoveryGroup)) {
      searchDiscoveryGroup.forEach(member => {
        expanded.add(normalizeColorForFiltering(member));
      });
    }
    
    // Then check ColorGroupsJSON (from theme settings)
    const groupKey = Object.keys(groupsMap).find(key => normalizeColorForFiltering(key) === normalized);
    if (groupKey && Array.isArray(groupsMap[groupKey])) {
      groupsMap[groupKey].forEach(member => {
        expanded.add(normalizeColorForFiltering(member));
      });
    }
  });
  
  const result = Array.from(expanded);
  return result;
};

/**
 * Apply filter selections to all product cards and product forms
 * @param {Object} activeFilters - Active filter values
 * @param {boolean} immediate - If true, apply immediately without delays
 */
const applyFiltersToProductCards = (activeFilters, immediate = false) => {
  // Prevent multiple simultaneous applications
  if (isApplyingFilters && !immediate) {
    return;
  }
  
  isApplyingFilters = true;
  
  try {
    // Build effective color selection by merging URL-resolved colors with
    // currently-selected color facets in the DOM (checked inputs / active swatches).
    // This is the authoritative "what the user selected" source and is essential for
    // multi-select where the URL uses GIDs the client can't resolve individually.
    const variantGridItemsProbe = document.querySelectorAll('.product-grid__item[data-variant-color]');
    const isVariantListingProbe = variantGridItemsProbe.length > 0;
    if (!isVariantListingProbe) {
      const domSelectedColors = [];
      document.querySelectorAll(
        '.facets__item .color-swatch.active[data-color], .facets__item .color-swatch[data-active="true"][data-color]'
      ).forEach((sw) => {
        if (sw instanceof HTMLElement && sw.dataset.color) domSelectedColors.push(sw.dataset.color);
      });
      document.querySelectorAll('.facets__item input[type="checkbox"]:checked').forEach((cb) => {
        if (!(cb instanceof HTMLInputElement)) return;
        if (!cb.name || !cb.name.toLowerCase().includes('color')) return;
        const parent = cb.closest('.color-swatch, li, label');
        if (parent instanceof HTMLElement) {
          const label = parent.dataset?.color || parent.getAttribute('data-color') || parent.getAttribute('title') || '';
          if (label) domSelectedColors.push(label);
        }
      });
      if (domSelectedColors.length > 0) {
        activeFilters.colors = Array.from(new Set([...(activeFilters.colors || []), ...domSelectedColors]));
      }
      // Re-apply selection-order priority after DOM merge so first-selected wins
      // (the Set de-dupe above may introduce colors in DOM order, not click order).
      activeFilters.colors = _orderColorsBySelection(activeFilters.colors);
    }

    if (activeFilters._serverFiltered && activeFilters.colors.length === 0) {
      // Server filtered on an unresolvable GID and we couldn't read any selection from
      // the DOM either — trust the server. Show everything it returned, untouched.
      const variantGridItems = document.querySelectorAll('.product-grid__item[data-variant-color]');
      const isVariantListing = variantGridItems.length > 0;

      if (!isVariantListing) {
        document.querySelectorAll('.sunice-product-card').forEach((card) => {
          const gi = card.closest('.product-grid__item, li');
          if (gi instanceof HTMLElement) gi.style.display = '';
        });
        const productGrid = document.querySelector('.product-grid, ul.product-grid');
        if (productGrid instanceof HTMLElement) productGrid.style.display = '';
        const existingEmptyMessage = document.querySelector('.main-collection-grid__empty, .filter-no-products-message');
        if (existingEmptyMessage instanceof HTMLElement) existingEmptyMessage.style.display = 'none';

        isApplyingFilters = false;
        return;
      }
    }

    if (!activeFilters || activeFilters.colors.length === 0) {
      // If no color filters, show all product cards and reset variant pickers to default
      // Check for variant listing mode by looking for grid items with data-variant-color
      const variantGridItems = document.querySelectorAll('.product-grid__item[data-variant-color]');
      const isVariantListing = variantGridItems.length > 0;
      
      if (isVariantListing) {
        // VARIANT LISTING MODE: Use VariantListingFilter to clear filters
        const globalWindow = /** @type {any} */ (window);
        if (globalWindow.VariantListingFilter && typeof globalWindow.VariantListingFilter.clearFilters === 'function') {
          globalWindow.VariantListingFilter.clearFilters();
        } else {
          // Fallback: Remove filter classes and restore pagination
          // Show ALL cards when no filter is active
          variantGridItems.forEach((gridItem) => {
            if (gridItem instanceof HTMLElement) {
              // No filter active = show ALL cards
              gridItem.classList.remove('filter-hidden');
              const page = parseInt(gridItem.dataset.page || '1', 10);
              if (page === 1) {
                gridItem.classList.remove('product-grid__item--hidden');
                gridItem.classList.add('active');
                gridItem.style.setProperty('display', 'block', 'important');
              } else {
                gridItem.classList.add('product-grid__item--hidden');
                gridItem.classList.remove('active');
                gridItem.style.setProperty('display', 'none', 'important');
              }
            }
          });
        }
        
        // Re-initialize variant card pickers after clearing filters
        setTimeout(() => {
          const globalWindow = /** @type {any} */ (window);
          if (globalWindow.setupVariantCardPickers && typeof globalWindow.setupVariantCardPickers === 'function') {
            globalWindow.setupVariantCardPickers();
          }
        }, 150);
      } else {
        // REGULAR PRODUCT CARD MODE
        const productCards = document.querySelectorAll('.sunice-product-card');
        productCards.forEach((productCard) => {
          const productGridItem = productCard.closest('.product-grid__item, li');
          if (productGridItem instanceof HTMLElement) {
            // Show all cards including those hidden by pagination
            productGridItem.style.display = '';
            productGridItem.classList.remove('product-grid__item--hidden');
          }
          resetVariantPickerToDefault(productCard);
        });
      }
      
      // Reset variant pickers in product forms
      const productForms = document.querySelectorAll('.product-form, .product-form-container');
      productForms.forEach((form) => {
        if (form instanceof HTMLElement) {
          resetVariantPickerToDefault(form);
        }
      });
      
      // Show all color filter options when filters are cleared
      const hiddenFilterItems = document.querySelectorAll('.facets__item li[data-no-products="true"], .facets__item .color-swatch[data-no-products="true"]');
      hiddenFilterItems.forEach((item) => {
        if (item instanceof HTMLElement) {
          item.style.display = '';
          item.removeAttribute('data-no-products');
          item.classList.remove('out-of-stock');
        }
      });
      
      // Also ensure all filter swatches are visible (in case they were hidden)
      const allFilterSwatches = document.querySelectorAll('.facets__item .color-swatch');
      allFilterSwatches.forEach((swatch) => {
        if (swatch instanceof HTMLElement) {
          const listItem = swatch.closest('li.facets__inputs-list-item');
          const targetElement = listItem instanceof HTMLElement ? listItem : swatch;
          targetElement.style.display = '';
        }
      });
      
      // Hide empty message
      const existingEmptyMessage = document.querySelector('.main-collection-grid__empty, .filter-no-products-message');
      if (existingEmptyMessage instanceof HTMLElement) {
        existingEmptyMessage.style.display = 'none';
      }
      
      const productGrid = document.querySelector('.product-grid, ul.product-grid');
      if (productGrid instanceof HTMLElement) {
        productGrid.style.display = '';
      }
      
      isApplyingFilters = false;
      return;
    }

    // Check for variant listing mode FIRST by looking for grid items with data-variant-color
    const variantGridItems = document.querySelectorAll('.product-grid__item[data-variant-color]');
    const isVariantListing = variantGridItems.length > 0;
    
    // If variant listing is enabled, use VariantListingFilter to handle filtering
    if (isVariantListing) {
      const updateVisibleCount = (count) => {
        const els = document.querySelectorAll('.products-count-wrapper span');
        els.forEach((el) => {
          if (!(el instanceof HTMLElement)) return;
          const txt = (el.textContent || '').trim();
          if (!txt) {
            el.textContent = String(count);
            return;
          }
          if (/\d/.test(txt)) {
            el.textContent = txt.replace(/\d[\d,]*/, String(count));
          } else {
            el.textContent = String(count);
          }
        });
      };

      const getMetafieldColor = (card) => {
        if (!(card instanceof HTMLElement)) return '';
        const fromLi = card.dataset.metafieldColor || '';
        if (fromLi) return fromLi;
        const inner = card.querySelector('.sunice-variant-product-card');
        if (inner instanceof HTMLElement) {
          return inner.getAttribute('data-metafield-color-filter') || '';
        }
        return '';
      };

      // Get color groups map and expand selected colors
      const selectedColors = activeFilters?.colors || [];
      const groupsMap = getColorGroupsMap();
      const allVariantColors = Array.from(document.querySelectorAll('.product-grid__item[data-variant-color]')).map(card => card.dataset.variantColor).filter(Boolean);
      const uniqueVariantColors = [...new Set(allVariantColors)];
      // Apply color filter using VariantListingFilter for strict matching
      const globalWindow = /** @type {any} */ (window);
      
      // CRITICAL: Always show grid before filtering - never hide it
      const productGrid = document.querySelector('.product-grid, ul.product-grid');
      if (productGrid instanceof HTMLElement) {
        productGrid.style.display = '';
        productGrid.classList.remove('filter-empty');
      }
      
      // Hide empty message initially
      const existingEmptyMessage = document.querySelector('.main-collection-grid__empty, .filter-no-products-message');
      if (existingEmptyMessage instanceof HTMLElement) {
        existingEmptyMessage.style.display = 'none';
      }
      
      // CRITICAL: Use VariantListingFilter.applyFilters() for strict data-color-group matching
      // Simple rule: NO data-color-group OR doesn't match = NO active class
      if (globalWindow.VariantListingFilter && typeof globalWindow.VariantListingFilter.applyFilters === 'function') {
        // Extract exact filter value from URL and resolve GIDs to labels
        const urlParams = new URLSearchParams(window.location.search);
        const exactFilterColors = [];
        let hasUnresolvedGid = false;
        for (const [key, value] of urlParams.entries()) {
          if (key.startsWith('filter.') && key.toLowerCase().includes('color')) {
            const decoded = safeDecodeURIComponent(value);
            if (decoded) {
              const resolved = resolveColorGid(decoded);
              if (resolved.includes('gid://') || resolved.toLowerCase().includes('filtersettinggroup')) {
                hasUnresolvedGid = true;
              } else {
                exactFilterColors.push(resolved);
              }
            }
          }
        }
        
        // Assign color groups first (this sets data-color-group on cards)
        if (typeof globalWindow.assignColorGroups === 'function') {
          globalWindow.assignColorGroups();
        }
        
        // If there's an unresolved GID and no resolved colors, server already handled filtering
        if (hasUnresolvedGid && exactFilterColors.length === 0) {
          // Trust server — don't hide any cards
        } else if (exactFilterColors.length > 0) {
          globalWindow.VariantListingFilter.applyFilters(exactFilterColors);
        }
        
        // DO NOT call applyColorFilter() - VariantListingFilter handles it with strict matching
      } else {
        // Fallback: Use the combined function from main-collection.liquid
        if (typeof globalWindow.assignColorGroupsAndFilter === 'function') {
          globalWindow.assignColorGroupsAndFilter();
        } else if (typeof globalWindow.assignColorGroups === 'function') {
          // Fallback: at least assign groups
          globalWindow.assignColorGroups();
          if (typeof globalWindow.applyColorFilter === 'function') {
            globalWindow.applyColorFilter();
          }
        }
      }
      
      // Count visible cards for product count update
      const visibleCards = document.querySelectorAll('.product-grid__item[data-variant-color]:not(.filter-hidden)');
      const result = { visible: visibleCards.length, total: document.querySelectorAll('.product-grid__item[data-variant-color]').length };
      updateVisibleCount(result.visible);

      // Show/hide empty message when no products match (e.g. color filter with no results)
      if (result && result.visible === 0) {
        // SAFETY NET: server already filtered — if we hid everything, undo it
        const hasUrlFilter = Array.from(new URLSearchParams(window.location.search).keys()).some(k => k.startsWith('filter.'));
        if (hasUrlFilter && result.total > 0) {
          variantGridItems.forEach((gi) => {
            if (gi instanceof HTMLElement) {
              gi.classList.remove('filter-hidden');
              gi.classList.add('active');
              gi.style.setProperty('display', 'block', 'important');
            }
          });
          if (productGrid instanceof HTMLElement) productGrid.style.display = '';
          if (existingEmptyMessage instanceof HTMLElement) existingEmptyMessage.style.display = 'none';
          updateVisibleCount(result.total);
          isApplyingFilters = false;
          return;
        }

        if (productGrid instanceof HTMLElement) {
          productGrid.style.display = 'none';
        }
        if (existingEmptyMessage instanceof HTMLElement) {
          existingEmptyMessage.style.display = '';
        } else {
          const emptyMessage = document.createElement('div');
          emptyMessage.className = 'main-collection-grid__empty filter-no-products-message';
          emptyMessage.innerHTML = `
          <h2 class="main-collection-grid__empty-title h2">No products available</h2>
          <p>There are no products available for the selected filter. <a href="${window.location.pathname}" class="main-collection-grid__empty-link">Clear filters</a></p>
        `;
          const resultsContainer = productGrid?.parentElement || document.querySelector('.collection-wrapper, results-list');
          if (resultsContainer instanceof HTMLElement) {
            resultsContainer.insertBefore(emptyMessage, productGrid);
          } else if (productGrid instanceof HTMLElement) {
            productGrid.parentElement?.insertBefore(emptyMessage, productGrid);
          }
        }
      } else {
        if (productGrid instanceof HTMLElement) {
          productGrid.style.display = '';
        }
        if (existingEmptyMessage instanceof HTMLElement) {
          existingEmptyMessage.style.display = 'none';
        }
      }
      
      // Re-initialize variant card pickers after filtering
      if (globalWindow.setupVariantCardPickers && typeof globalWindow.setupVariantCardPickers === 'function') {
        globalWindow.setupVariantCardPickers();
      }
      
      // Reset flag
      isApplyingFilters = false;
      return;
    }
    
    let visibleCount = 0;
    let hiddenCount = 0;
    
    // REGULAR PRODUCT CARD MODE: Use existing logic
    const productCards = document.querySelectorAll('.sunice-product-card');
    if (productCards.length === 0) {
      // During section transitions DOM can be temporarily empty; retry once before showing empty state.
      setTimeout(() => {
        const retryFilters = extractActiveFilters(new URLSearchParams(window.location.search));
        applyFiltersToProductCards(retryFilters, true);
      }, 120);
      return;
    }
    
    // CRITICAL: Hide all products FIRST to prevent visual glitch
    productCards.forEach((productCard) => {
      const productGridItem = productCard.closest('.product-grid__item, li');
      if (productGridItem instanceof HTMLElement) {
        productGridItem.style.display = 'none';
      }
    });
    
    // Show each product, swap to the filter color variant, then hide if that color is OOS.
    // updateProductCardForFilter returns false when the color swiper has no matching swatch,
    // which means the filter color is not available (swiper only renders available variants).
    productCards.forEach((productCard) => {
      if (!(productCard instanceof HTMLElement)) return;

      const productGridItem = productCard.closest('.product-grid__item, li');
      if (!(productGridItem instanceof HTMLElement)) return;

      productGridItem.style.display = '';
      const isAvailable = updateProductCardForFilter(productCard, activeFilters);
      if (isAvailable) {
        visibleCount++;
      } else {
        productGridItem.style.display = 'none';
        hiddenCount++;
      }
    });
    
    // Update visible product count
    updateProductCount(visibleCount);

    // Show/hide "no products" message
    const productGrid = document.querySelector('.product-grid, ul.product-grid');
    const existingEmptyMessage = document.querySelector('.main-collection-grid__empty, .filter-no-products-message');
    
    if (visibleCount === 0) {
      // SAFETY NET: If URL has filter params, the server already returned filtered products.
      // If our client-side check found 0 matches, it's a mismatch — show all server products.
      const hasUrlFilter = Array.from(new URLSearchParams(window.location.search).keys()).some(k => k.startsWith('filter.'));
      if ((hasUrlFilter || activeFilters.colors.length > 0 || activeFilters._serverFiltered) && productCards.length > 0) {
        productCards.forEach((pc) => {
          const gi = pc.closest('.product-grid__item, li');
          if (gi instanceof HTMLElement) {
            gi.style.display = '';
          }
        });
        if (productGrid instanceof HTMLElement) productGrid.style.display = '';
        if (existingEmptyMessage instanceof HTMLElement) existingEmptyMessage.style.display = 'none';
        updateProductCount(productCards.length);
        isApplyingFilters = false;
        return;
      }

      // Hide the product grid
      if (productGrid instanceof HTMLElement) {
        productGrid.style.display = 'none';
      }
      
      // Show or create empty message
      if (existingEmptyMessage instanceof HTMLElement) {
        existingEmptyMessage.style.display = '';
      } else {
        const emptyMessage = document.createElement('div');
        emptyMessage.className = 'main-collection-grid__empty filter-no-products-message';
        emptyMessage.innerHTML = `
          <h2 class="main-collection-grid__empty-title h2">No products available</h2>
          <p>There are no products available for the selected filter. <a href="${window.location.pathname}" class="main-collection-grid__empty-link">Clear filters</a></p>
        `;
        
        const resultsContainer = productGrid?.parentElement || document.querySelector('.collection-wrapper, results-list');
        if (resultsContainer instanceof HTMLElement) {
          resultsContainer.insertBefore(emptyMessage, productGrid);
        } else if (productGrid instanceof HTMLElement) {
          productGrid.parentElement?.insertBefore(emptyMessage, productGrid);
        }
      }
    } else {
      // Show the product grid
      if (productGrid instanceof HTMLElement) {
        productGrid.style.display = '';
      }
      
      // Hide empty message
      if (existingEmptyMessage instanceof HTMLElement) {
        existingEmptyMessage.style.display = 'none';
      }
    }
    
    // CRITICAL: Update variant pickers in visible product cards to select the correct variant
    // This ensures the correct color, size, images, and variant ID are set based on the filter
    // NOTE: Variant cards don't need variant selection - they already show a specific variant
    // Only do this for regular product cards, not variant listing mode
    const variantGridItemsCheck = document.querySelectorAll('.product-grid__item[data-variant-color]');
    if (visibleCount > 0 && variantGridItemsCheck.length === 0) {
      setTimeout(() => {
        const visibleProductCards = Array.from(productCards).filter((card) => {
          const gridItem = card.closest('.product-grid__item, li');
          return gridItem instanceof HTMLElement && gridItem.style.display !== 'none';
        });
        
        visibleProductCards.forEach((productCard) => {
          if (!(productCard instanceof HTMLElement)) return;
          selectVariantFromFilter(productCard, activeFilters);
        });
      }, 150);
    }
  } finally {
    isApplyingFilters = false;
    // Update color filter availability after filters are applied
    updateColorFilterAvailabilityAfterFilters();
  }
};

/**
 * Update data-option-available on color filter swatches based on visible products after filters are applied
 */
const updateColorFilterAvailabilityAfterFilters = () => {
  // Get all visible product cards (not hidden by filters)
  const visibleCards = Array.from(document.querySelectorAll('.product-grid__item.active:not(.filter-hidden)'));
  
  // Collect all available colors from visible cards
  const availableColors = new Set();
  visibleCards.forEach((card) => {
    if (card instanceof HTMLElement) {
      const variantColor = card.getAttribute('data-variant-color');
      if (variantColor) {
        availableColors.add(normalizeString(variantColor));
      }
    }
  });
  
  // Update all color filter swatches
  const allColorSwatches = document.querySelectorAll('.facets__item .color-swatch[data-color]');
  allColorSwatches.forEach((swatch) => {
    if (!(swatch instanceof HTMLElement)) return;
    
    const swatchColor = swatch.dataset.color || '';
    const normalizedColor = normalizeString(swatchColor);
    
    // Check if this color has available products in the visible cards
    const hasAvailableProducts = availableColors.has(normalizedColor) || 
                                 Array.from(availableColors).some(color => 
                                   color.includes(normalizedColor) || normalizedColor.includes(color)
                                 );
    
    // Update data-option-available attribute
    if (hasAvailableProducts || visibleCards.length === 0) {
      // If there are visible products with this color, or no products visible (might be loading), mark as available
      swatch.setAttribute('data-option-available', 'true');
    } else {
      swatch.setAttribute('data-option-available', 'false');
    }
  });
};

/**
 * Re-initialize Sunice-specific behaviors (color swipers, product card components)
 * after the collection/search section has been re-rendered by filters.
 */
const reinitializeSuniceAfterFilters = () => {
  try {
    /** @type {any} */
    const globalWindow = window;

    // Restore color swatch backgrounds if they were preserved
    // IMPORTANT: restoreColorSwatchBackgrounds only restores FILTER swatches, NOT product card swatches
    // Product card swatch backgrounds should remain as rendered in HTML
    const preservedSwatches = globalWindow._preservedColorSwatches;
    if (preservedSwatches instanceof Map) {
      restoreColorSwatchBackgrounds(preservedSwatches);
      // Clear after use
      globalWindow._preservedColorSwatches = null;
    }

    // Extract active filters from URL
    const urlParams = new URLSearchParams(window.location.search);
    const activeFilters = extractActiveFilters(urlParams);

    // Re-init color swipers
    const ColorSwiperConstructor = globalWindow.SuniceColorSwiper;
    if (typeof ColorSwiperConstructor === 'function') {
      // Prefer the shared singleton instance created in sunice-color-swiper.js, if present
      const instance =
        globalWindow.SuniceColorSwiperInstance || new ColorSwiperConstructor();
      if (!globalWindow.SuniceColorSwiperInstance) {
        globalWindow.SuniceColorSwiperInstance = instance;
      }

      // Find all current color swiper containers in the DOM
      const containers = document.querySelectorAll('.color-swiper-container');
      const currentContainerIds = new Set();
      
      containers.forEach((container) => {
        if (container instanceof HTMLElement) {
          const containerId = container.id || `color-swiper-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
          if (!container.id) {
            container.id = containerId;
          }
          currentContainerIds.add(container.id);
        }
      });

      // Clean up any swiper instances whose DOM elements were removed OR
      // destroy existing instances for containers that still exist (to force re-init)
      try {
        if (instance.swiperInstances && typeof instance.swiperInstances.forEach === 'function') {
          instance.swiperInstances.forEach((swiper, id) => {
            try {
              const el = swiper && swiper.el;
              const isRemoved = !el || !(el instanceof HTMLElement) || !document.body.contains(el);
              const needsReinit = currentContainerIds.has(id);
              
              // Destroy if removed from DOM OR if it still exists (to force re-init)
              if (isRemoved || needsReinit) {
                if (swiper && typeof swiper.destroy === 'function') {
                  swiper.destroy(true, true);
                }
                instance.swiperInstances.delete(id);
                
                // If container still exists, reset its initialization state
                if (needsReinit && !isRemoved && el) {
                  el.dataset.suniceColorSwiperInitialized = 'false';
                  // Reset classes that might have been set
                  el.classList.remove('swiper', 'color-swiper', 'no-swatch-slider');
                  const wrapper = el.querySelector('.color-swiper-wrapper');
                  if (wrapper) {
                    wrapper.classList.remove('swiper-wrapper');
                  }
                  const slides = el.querySelectorAll('.color-swiper-slide');
                  slides.forEach((slide) => {
                    if (slide instanceof HTMLElement) {
                      slide.classList.remove('swiper-slide');
                    }
                  });
                }
              }
            } catch (e) {
              console.warn('Error cleaning up color swiper instance:', id, e);
            }
          });
        }
      } catch (e) {
        console.warn('Error during color swiper cleanup:', e);
      }

      // Now initialize all current containers (they should be clean now)
      containers.forEach((container) => {
        if (container instanceof HTMLElement) {
          // Ensure data attribute is reset
          container.dataset.suniceColorSwiperInitialized = 'false';
          instance.initializeColorSwiper(container);
        }
      });
    }
    // Re-init native swatch carousels
    if (globalWindow.SuniceSwatchCarousel && typeof globalWindow.SuniceSwatchCarousel.init === 'function') {
      globalWindow.SuniceSwatchCarousel.init(document);
    }

    // Re-init Sunice product card variant pickers / behaviors
    if (typeof globalWindow.initializeSuniceProductCardComponents === 'function') {
      globalWindow.initializeSuniceProductCardComponents();
    }
    
    // Re-init variant card pickers specifically (for variant product cards)
    if (typeof globalWindow.setupVariantCardPickers === 'function') {
      globalWindow.setupVariantCardPickers();
    }

    // NOTE: Filtering is now handled by runReinit() in updateSection()
    // We removed the duplicate call here to prevent double-filtering issues
    // Only hide unavailable filters if NO filters are active (this happens after filtering in runReinit)
  } catch (e) {
    // Swallow errors so core filter behavior is never blocked
    console.warn('Sunice: error reinitializing components after filter update:', e);
  }
};

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    // Only include price filters if they differ from defaults (user actually interacted with slider)
    const priceGte = newParameters.get('filter.v.price.gte');
    const priceLte = newParameters.get('filter.v.price.lte');
    
    // Find price facet component to check if values are at defaults
    const priceFacet = document.querySelector('price-facet-component');
    if (priceFacet instanceof HTMLElement) {
      const priceComponent = /** @type {any} */ (priceFacet);
      if (priceComponent.refs?.minInput && priceComponent.refs?.maxInput) {
        const minInput = priceComponent.refs.minInput;
        const maxInput = priceComponent.refs.maxInput;
        const minDefault = minInput.getAttribute('min') || minInput.min;
        const maxDefault = maxInput.getAttribute('max') || maxInput.max;
        
        // Convert price params from dollars to cents for comparison
        const gteCents = priceGte ? Math.round(parseFloat(priceGte) * 100) : null;
        const lteCents = priceLte ? Math.round(parseFloat(priceLte) * 100) : null;
        const minDefaultCents = parseInt(minDefault, 10);
        const maxDefaultCents = parseInt(maxDefault, 10);
        
        // Remove price params if they're at defaults (user didn't interact with slider)
        if (gteCents === minDefaultCents) {
          newParameters.delete('filter.v.price.gte');
        }
        if (lteCents === maxDefaultCents) {
          newParameters.delete('filter.v.price.lte');
        }
      }
    }
    
    // Also remove empty price params
    if (priceGte === '' || !priceGte) newParameters.delete('filter.v.price.gte');
    if (priceLte === '' || !priceLte) newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters() {
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.updateSection();
  }

  /**
   * Updates the section
   */
  updateSection() {
    const viewTransition = !this.closest('dialog');

    // Preserve color swatch backgrounds before section re-render
    const preservedSwatches = preserveColorSwatchBackgrounds();
    if (preservedSwatches.size > 0) {
      /** @type {any} */ (window)._preservedColorSwatches = preservedSwatches;
    }
    
    // CRITICAL: Preserve the entire filters DOM to prevent it from being removed/re-added
    const filtersWrapper = document.querySelector('.facets__filters-wrapper');
    const filtersFormWrapper = this.querySelector('.facets__form-wrapper');
    let preservedFiltersHTML = null;
    let preservedFiltersParent = null;
    
    if (filtersWrapper instanceof HTMLElement) {
      preservedFiltersHTML = filtersWrapper.innerHTML;
      preservedFiltersParent = filtersWrapper.parentElement;
    } else if (filtersFormWrapper instanceof HTMLElement) {
      // Fallback: preserve the entire form wrapper
      preservedFiltersHTML = filtersFormWrapper.innerHTML;
      preservedFiltersParent = filtersFormWrapper.parentElement;
    }
    
    // CRITICAL: Preserve checkbox states before section update
    const checkboxStates = new Map();
    const allInputs = document.querySelectorAll('.facets__item input[type="checkbox"], .facets__item input[type="radio"]');
    allInputs.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const inputId = input.id || input.name + '_' + input.value;
      checkboxStates.set(inputId, {
        checked: input.checked,
        value: input.value,
        name: input.name
      });
    });

    // Helper to run re-init after section update
    // OPTIMIZED: Near-instant filter response - no delays
    const runReinit = () => {
      // Apply filters immediately - DOM should already be updated
      // Use requestAnimationFrame only to batch with browser's layout cycle
      requestAnimationFrame(() => {
            // 1. Reinitialize Sunice components (color swipers, etc.)
            reinitializeSuniceAfterFilters();
            
            // 2. Re-init VariantListingFilter (idempotent, safe to call)
            const globalWindow = /** @type {any} */ (window);
            if (globalWindow.VariantListingFilter && typeof globalWindow.VariantListingFilter.init === 'function') {
              globalWindow.VariantListingFilter.init();
            }
            
            // 3. Extract and apply filters ONCE - just show listings
            const urlParams = new URLSearchParams(window.location.search);
            const activeFilters = extractActiveFilters(urlParams);
            
            // CRITICAL: Make sure grid is visible BEFORE checking for cards
            const productGrid = document.querySelector('.product-grid, ul.product-grid');
            if (productGrid instanceof HTMLElement) {
              productGrid.style.display = '';
              productGrid.classList.remove('filter-empty');
              console.log('[Facets] runReinit - Grid made visible, display:', productGrid.style.display);
            } else {
              console.warn('[Facets] runReinit - Grid NOT found in DOM!');
            }
            
            // Check if variant cards exist in DOM
            const variantCards = document.querySelectorAll('.product-grid__item[data-variant-color]');
            const allGridItems = document.querySelectorAll('.product-grid__item');
            console.log('[Facets] runReinit - variant cards:', variantCards.length, 'all grid items:', allGridItems.length);
            
            if (variantCards.length === 0 && allGridItems.length > 0) {
              console.warn('[Facets] runReinit - Grid items exist but NO data-variant-color attributes! Cards might not be rendered yet.');
            }
            
            // Apply color filter - use VariantListingFilter for strict data-color-group matching (same as applyFiltersToProductCards)
            if (variantCards.length > 0) {
              const urlParams = new URLSearchParams(window.location.search);
              const exactFilterColors = [];
              let hasUnresolvedGid = false;
              for (const [key, value] of urlParams.entries()) {
                if (key.startsWith('filter.') && key.toLowerCase().includes('color')) {
                  const decoded = safeDecodeURIComponent(value);
                  if (decoded) {
                    const resolved = resolveColorGid(decoded);
                    if (resolved.includes('gid://') || resolved.toLowerCase().includes('filtersettinggroup')) {
                      hasUnresolvedGid = true;
                    } else {
                      exactFilterColors.push(resolved);
                    }
                  }
                }
              }
              if (globalWindow.VariantListingFilter && typeof globalWindow.VariantListingFilter.applyFilters === 'function') {
                if (typeof globalWindow.assignColorGroups === 'function') {
                  globalWindow.assignColorGroups();
                }
                if (hasUnresolvedGid && exactFilterColors.length === 0) {
                  // GID couldn't be resolved — server already filtered, trust it
                } else {
                  globalWindow.VariantListingFilter.applyFilters(exactFilterColors);
                }
              } else if (typeof globalWindow.assignColorGroupsAndFilter === 'function') {
                globalWindow.assignColorGroupsAndFilter();
              } else {
                if (typeof globalWindow.assignColorGroups === 'function') {
                  globalWindow.assignColorGroups();
                }
                if (typeof globalWindow.applyColorFilter === 'function') {
                  globalWindow.applyColorFilter();
                }
              }

              // Show empty message and update product count when color filter yields no results
              requestAnimationFrame(() => {
                const visibleCards = document.querySelectorAll('.product-grid__item[data-variant-color]:not(.filter-hidden)');
                const existingEmptyMessage = document.querySelector('.main-collection-grid__empty, .filter-no-products-message');
                // Update product count (including 0 when no match)
                const countEls = document.querySelectorAll('.products-count-wrapper span');
                countEls.forEach((el) => {
                  if (el instanceof HTMLElement) {
                    const txt = (el.textContent || '').trim();
                    if (/\d/.test(txt)) {
                      el.textContent = txt.replace(/\d[\d,]*/, String(visibleCards.length));
                    } else {
                      el.textContent = String(visibleCards.length);
                    }
                  }
                });

                if (visibleCards.length === 0) {
                  // SAFETY NET: server already filtered — show all cards if URL has filters
                  const allVCards = document.querySelectorAll('.product-grid__item[data-variant-color]');
                  const hasUrlFilter = Array.from(new URLSearchParams(window.location.search).keys()).some(k => k.startsWith('filter.'));
                  if (hasUrlFilter && allVCards.length > 0) {
                    allVCards.forEach((c) => { if (c instanceof HTMLElement) { c.classList.remove('filter-hidden'); c.classList.add('active'); c.style.setProperty('display', 'block', 'important'); }});
                    if (productGrid instanceof HTMLElement) productGrid.style.display = '';
                    if (existingEmptyMessage instanceof HTMLElement) existingEmptyMessage.style.display = 'none';
                  } else {
                    if (productGrid instanceof HTMLElement) {
                      productGrid.style.display = 'none';
                    }
                    if (existingEmptyMessage instanceof HTMLElement) {
                      existingEmptyMessage.style.display = '';
                    } else {
                      const emptyMessage = document.createElement('div');
                      emptyMessage.className = 'main-collection-grid__empty filter-no-products-message';
                      emptyMessage.innerHTML = `
                        <h2 class="main-collection-grid__empty-title h2">No products available</h2>
                        <p>There are no products available for the selected filter. <a href="${window.location.pathname}" class="main-collection-grid__empty-link">Clear filters</a></p>
                      `;
                      const resultsContainer = productGrid?.parentElement || document.querySelector('.collection-wrapper, results-list');
                      if (resultsContainer instanceof HTMLElement) {
                        resultsContainer.insertBefore(emptyMessage, productGrid);
                      } else if (productGrid instanceof HTMLElement) {
                        productGrid.parentElement?.insertBefore(emptyMessage, productGrid);
                      }
                    }
                  }
                } else {
                  if (productGrid instanceof HTMLElement) {
                    productGrid.style.display = '';
                  }
                  if (existingEmptyMessage instanceof HTMLElement) {
                    existingEmptyMessage.style.display = 'none';
                  }
                }
              });
            } else {
              // REGULAR PRODUCT CARD MODE: Apply filters to swap images to matching variant
              if (activeFilters.colors.length > 0 || activeFilters._serverFiltered) {
                applyFiltersToProductCards(activeFilters, true);
              }
              if (productGrid instanceof HTMLElement) {
                productGrid.style.display = '';
              }
            }
            
            // Ensure ALL filter values are ALWAYS visible - never hide any filters
            const allFilterSwatches = document.querySelectorAll('.facets__item .color-swatch');
            allFilterSwatches.forEach((swatch) => {
              if (swatch instanceof HTMLElement) {
                const listItem = swatch.closest('li.facets__inputs-list-item');
                const targetElement = listItem instanceof HTMLElement ? listItem : swatch;
                targetElement.style.display = '';
                targetElement.removeAttribute('data-no-products');
                targetElement.classList.remove('out-of-stock');
              }
            });
            
            // Never hide any filters - ensure everything stays visible
            hideUnavailableColorFilters();
            
            // CRITICAL: Update active states on ALL filter types after DOM is restored
            // This ensures selected filters maintain their active state
            updateFilterButtonActiveStates(activeFilters);
            
            // Also update active states for other filter types (price, product type, etc.)
            // by syncing with URL parameters
            // urlParams already declared above, reuse it
            syncAllFilterActiveStates(urlParams);
            
            // 4. Dispatch facets:update AFTER filters are applied (for other components)
            // NO MORE FILTERING - we're done
            document.dispatchEvent(new CustomEvent('facets:update'));
            
            // 5. Re-initialize variant card scroll reveal and swiper (defer to avoid blocking)
            requestAnimationFrame(() => {
              const globalWindow = /** @type {any} */ (window);
              if (globalWindow.VariantCardScrollReveal && typeof globalWindow.VariantCardScrollReveal.init === 'function') {
                globalWindow.VariantCardScrollReveal.init();
              }
              if (globalWindow.suniceSwiperInfinite && typeof globalWindow.suniceSwiperInfinite.initializeMainSwipers === 'function') {
                globalWindow.suniceSwiperInfinite.initializeMainSwipers(0);
              }
            });
        });
      };

    if (viewTransition) {
      startViewTransition(() => {
        const result = sectionRenderer.renderSection(this.sectionId).catch((error) => {
          // Handle section rendering errors gracefully
          // This can happen with 401 errors in development or when sections aren't available
          console.warn('Section rendering failed, but continuing:', error);
          return null;
        });

        // Ensure Sunice re-init runs after the section HTML has been updated
        if (result && typeof /** @type {any} */ (result).then === 'function') {
          /** @type {Promise<any>} */ (result).then(() => {
            // CRITICAL: Restore preserved filters HTML to prevent filter removal/re-addition
            if (preservedFiltersHTML && preservedFiltersParent) {
              const currentFiltersWrapper = preservedFiltersParent.querySelector('.facets__filters-wrapper');
              const currentFiltersFormWrapper = preservedFiltersParent.querySelector('.facets__form-wrapper');
              
              if (currentFiltersWrapper instanceof HTMLElement) {
                currentFiltersWrapper.innerHTML = preservedFiltersHTML;
              } else if (currentFiltersFormWrapper instanceof HTMLElement) {
                currentFiltersFormWrapper.innerHTML = preservedFiltersHTML;
              }
              
              // CRITICAL: Restore active states immediately after restoring HTML
              const urlParams = new URLSearchParams(window.location.search);
              const activeFilters = extractActiveFilters(urlParams);
              updateFilterButtonActiveStates(activeFilters);
              syncAllFilterActiveStates(urlParams);
            }
            runReinit();
          });
        } else {
          // CRITICAL: Restore preserved filters HTML to prevent filter removal/re-addition
          if (preservedFiltersHTML && preservedFiltersParent) {
            const currentFiltersWrapper = preservedFiltersParent.querySelector('.facets__filters-wrapper');
            const currentFiltersFormWrapper = preservedFiltersParent.querySelector('.facets__form-wrapper');
            
            if (currentFiltersWrapper instanceof HTMLElement) {
              currentFiltersWrapper.innerHTML = preservedFiltersHTML;
            } else if (currentFiltersFormWrapper instanceof HTMLElement) {
              currentFiltersFormWrapper.innerHTML = preservedFiltersHTML;
            }
            
            // CRITICAL: Restore checkbox states from the preserved states
            // This ensures checkboxes stay checked after user interaction (aesthetic only)
            if (checkboxStates.size > 0) {
              const restoredInputs = document.querySelectorAll('.facets__item input[type="checkbox"], .facets__item input[type="radio"]');
              restoredInputs.forEach((input) => {
                if (!(input instanceof HTMLInputElement)) return;
                const inputId = input.id || input.name + '_' + input.value;
                const preservedState = checkboxStates.get(inputId);
                if (preservedState) {
                  input.checked = preservedState.checked;
                  
                  const swatch = input.closest('.color-swatch');
                  const pillLabel = input.closest('.facets__pill-label') || input.parentElement?.querySelector('.facets__pill-label');
                  
                  if (swatch instanceof HTMLElement) {
                    if (preservedState.checked) {
                      swatch.classList.add('active');
                      swatch.setAttribute('data-active', 'true');
                    } else {
                      swatch.classList.remove('active');
                      swatch.removeAttribute('data-active');
                    }
                  }
                  
                  if (pillLabel instanceof HTMLElement) {
                    if (preservedState.checked) {
                      pillLabel.classList.add('active');
                    } else {
                      pillLabel.classList.remove('active');
                    }
                  }
                }
              });
              }
              
              // CRITICAL: Always sync with URL to ensure active states match URL params
              // The URL was already updated by #updateURLHash() before section re-render
              const urlParams = new URLSearchParams(window.location.search);
              const activeFilters = extractActiveFilters(urlParams);
              updateFilterButtonActiveStates(activeFilters);
              syncAllFilterActiveStates(urlParams);
          }
          // Fallback for non-promise implementations
          runReinit();
        }
      }, ['product-grid']);
    } else {
      const result = sectionRenderer.renderSection(this.sectionId).catch((error) => {
        // Handle section rendering errors gracefully
        // This can happen with 401 errors in development or when sections aren't available
        console.warn('Section rendering failed, but continuing:', error);
        return null;
      });
      if (result && typeof /** @type {any} */ (result).then === 'function') {
        /** @type {Promise<any>} */ (result).then(() => {
          // CRITICAL: Restore preserved filters HTML to prevent filter removal/re-addition
          if (preservedFiltersHTML && preservedFiltersParent) {
            const currentFiltersWrapper = preservedFiltersParent.querySelector('.facets__filters-wrapper');
            const currentFiltersFormWrapper = preservedFiltersParent.querySelector('.facets__form-wrapper');
            
            if (currentFiltersWrapper instanceof HTMLElement) {
              currentFiltersWrapper.innerHTML = preservedFiltersHTML;
            } else if (currentFiltersFormWrapper instanceof HTMLElement) {
              currentFiltersFormWrapper.innerHTML = preservedFiltersHTML;
            }
            
            // CRITICAL: Restore checkbox states from the preserved states
            // This ensures checkboxes stay checked after user interaction (aesthetic only)
            if (checkboxStates.size > 0) {
              const restoredInputs = document.querySelectorAll('.facets__item input[type="checkbox"], .facets__item input[type="radio"]');
              restoredInputs.forEach((input) => {
                if (!(input instanceof HTMLInputElement)) return;
                const inputId = input.id || input.name + '_' + input.value;
                const preservedState = checkboxStates.get(inputId);
                if (preservedState) {
                  input.checked = preservedState.checked;
                  
                  const swatch = input.closest('.color-swatch');
                  const pillLabel = input.closest('.facets__pill-label') || input.parentElement?.querySelector('.facets__pill-label');
                  
                  if (swatch instanceof HTMLElement) {
                    if (preservedState.checked) {
                      swatch.classList.add('active');
                      swatch.setAttribute('data-active', 'true');
                    } else {
                      swatch.classList.remove('active');
                      swatch.removeAttribute('data-active');
                    }
                  }
                  
                  if (pillLabel instanceof HTMLElement) {
                    if (preservedState.checked) {
                      pillLabel.classList.add('active');
                    } else {
                      pillLabel.classList.remove('active');
                    }
                  }
                }
              });
              }
              
              // CRITICAL: Always sync with URL to ensure active states match URL params
              // The URL was already updated by #updateURLHash() before section re-render
              const urlParams = new URLSearchParams(window.location.search);
              const activeFilters = extractActiveFilters(urlParams);
              updateFilterButtonActiveStates(activeFilters);
              syncAllFilterActiveStates(urlParams);
          }
          runReinit();
        }).catch((error) => {
          // Even if rendering fails, still restore filters and run reinit
          if (preservedFiltersHTML && preservedFiltersParent) {
            const currentFiltersWrapper = preservedFiltersParent.querySelector('.facets__filters-wrapper');
            const currentFiltersFormWrapper = preservedFiltersParent.querySelector('.facets__form-wrapper');
            
            if (currentFiltersWrapper instanceof HTMLElement) {
              currentFiltersWrapper.innerHTML = preservedFiltersHTML;
            } else if (currentFiltersFormWrapper instanceof HTMLElement) {
              currentFiltersFormWrapper.innerHTML = preservedFiltersHTML;
            }
            
            // CRITICAL: Restore checkbox states from the preserved states
            // This ensures checkboxes stay checked after user interaction (aesthetic only)
            if (checkboxStates.size > 0) {
              const restoredInputs = document.querySelectorAll('.facets__item input[type="checkbox"], .facets__item input[type="radio"]');
              restoredInputs.forEach((input) => {
                if (!(input instanceof HTMLInputElement)) return;
                const inputId = input.id || input.name + '_' + input.value;
                const preservedState = checkboxStates.get(inputId);
                if (preservedState) {
                  input.checked = preservedState.checked;
                  
                  const swatch = input.closest('.color-swatch');
                  const pillLabel = input.closest('.facets__pill-label') || input.parentElement?.querySelector('.facets__pill-label');
                  
                  if (swatch instanceof HTMLElement) {
                    if (preservedState.checked) {
                      swatch.classList.add('active');
                      swatch.setAttribute('data-active', 'true');
                    } else {
                      swatch.classList.remove('active');
                      swatch.removeAttribute('data-active');
                    }
                  }
                  
                  if (pillLabel instanceof HTMLElement) {
                    if (preservedState.checked) {
                      pillLabel.classList.add('active');
                    } else {
                      pillLabel.classList.remove('active');
                    }
                  }
                }
              });
              }
              
              // CRITICAL: Always sync with URL to ensure active states match URL params
              // The URL was already updated by #updateURLHash() before section re-render
              const urlParams = new URLSearchParams(window.location.search);
              const activeFilters = extractActiveFilters(urlParams);
              updateFilterButtonActiveStates(activeFilters);
              syncAllFilterActiveStates(urlParams);
          }
          console.warn('Section rendering promise rejected, but running reinit anyway:', error);
          runReinit();
        });
      } else {
        // CRITICAL: Restore preserved filters HTML to prevent filter removal/re-addition
        if (preservedFiltersHTML && preservedFiltersParent) {
          const currentFiltersWrapper = preservedFiltersParent.querySelector('.facets__filters-wrapper');
          const currentFiltersFormWrapper = preservedFiltersParent.querySelector('.facets__form-wrapper');
          
          if (currentFiltersWrapper instanceof HTMLElement) {
            currentFiltersWrapper.innerHTML = preservedFiltersHTML;
          } else if (currentFiltersFormWrapper instanceof HTMLElement) {
            currentFiltersFormWrapper.innerHTML = preservedFiltersHTML;
          }
          
          // CRITICAL: Restore checkbox states from the preserved states
          if (checkboxStates.size > 0) {
            const restoredInputs = document.querySelectorAll('.facets__item input[type="checkbox"], .facets__item input[type="radio"]');
            restoredInputs.forEach((input) => {
              if (!(input instanceof HTMLInputElement)) return;
              const inputId = input.id || input.name + '_' + input.value;
              const preservedState = checkboxStates.get(inputId);
              if (preservedState) {
                input.checked = preservedState.checked;
                
                const swatch = input.closest('.color-swatch');
                const pillLabel = input.closest('.facets__pill-label') || input.parentElement?.querySelector('.facets__pill-label');
                
                if (swatch instanceof HTMLElement) {
                  if (preservedState.checked) {
                    swatch.classList.add('active');
                    swatch.setAttribute('data-active', 'true');
                  } else {
                    swatch.classList.remove('active');
                    swatch.removeAttribute('data-active');
                  }
                }
                
                if (pillLabel instanceof HTMLElement) {
                  if (preservedState.checked) {
                    pillLabel.classList.add('active');
                  } else {
                    pillLabel.classList.remove('active');
                  }
                }
              }
            });
          }
          
          // CRITICAL: Restore active states immediately after restoring HTML
          const urlParams = new URLSearchParams(window.location.search);
          const activeFilters = extractActiveFilters(urlParams);
          updateFilterButtonActiveStates(activeFilters);
          syncAllFilterActiveStates(urlParams);
        }
        runReinit();
      }
    }
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    history.pushState('', '', url);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.updateSection();
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price slider
 * @property {HTMLInputElement} maxInput - The maximum price slider
 * @property {HTMLInputElement} minHiddenInput - The hidden input for min price (dollars)
 * @property {HTMLInputElement} maxHiddenInput - The hidden input for max price (dollars)
 * @property {HTMLElement} minDisplay - The minimum price display element
 * @property {HTMLElement} maxDisplay - The maximum price display element
 * @property {HTMLElement} sliderRange - The slider range indicator
 */
/**
 * Handles price facet functionality with dual-range slider
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  #debouncedUpdate = debounce(() => {
    this.updatePriceFilterAndResults();
  }, 300);

  connectedCallback() {
    super.connectedCallback();
    this.#restoreValuesFromURL();
    this.#initializeSlider();
    this.#updateSliderRange();
    this.#updateDisplay();
    
    // Listen for filter updates to restore values after section re-render
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Handles filter update events to restore slider values
   */
  #handleFilterUpdate = () => {
    // Restore values from URL after section update
    requestAnimationFrame(() => {
      this.#restoreValuesFromURL();
      this.#updateSliderRange();
      this.#updateDisplay();
    });
  };

  /**
   * Restores slider values from URL parameters
   */
  #restoreValuesFromURL() {
    const { minInput, maxInput } = this.refs;
    if (!minInput || !maxInput) return;

    const url = new URL(window.location.href);
    const minParam = url.searchParams.get('filter.v.price.gte');
    const maxParam = url.searchParams.get('filter.v.price.lte');

    if (minParam) {
      // Convert from dollars to cents
      const minValue = Math.round(parseFloat(minParam) * 100);
      if (minValue >= parseInt(minInput.min, 10) && minValue <= parseInt(minInput.max, 10)) {
        minInput.value = minValue.toString();
      }
    }

    if (maxParam) {
      // Convert from dollars to cents
      const maxValue = Math.round(parseFloat(maxParam) * 100);
      if (maxValue >= parseInt(maxInput.min, 10) && maxValue <= parseInt(maxInput.max, 10)) {
        maxInput.value = maxValue.toString();
      }
    }
  }

  /**
   * Initializes the slider functionality
   */
  #initializeSlider() {
    const { minInput, maxInput } = this.refs;
    if (!minInput || !maxInput) return;

    // Ensure min doesn't exceed max and vice versa
    minInput.addEventListener('input', () => {
      const minValue = parseInt(minInput.value, 10);
      const maxValue = parseInt(maxInput.value, 10);
      
      if (minValue > maxValue) {
        minInput.value = maxValue.toString();
      }
      
      this.#updateDisplay();
      this.#updateSliderRange();
      // Use debounced update to prevent too frequent re-renders
      this.#debouncedUpdate();
    });

    maxInput.addEventListener('input', () => {
      const minValue = parseInt(minInput.value, 10);
      const maxValue = parseInt(maxInput.value, 10);
      
      if (maxValue < minValue) {
        maxInput.value = minValue.toString();
      }
      
      this.#updateDisplay();
      this.#updateSliderRange();
      // Use debounced update to prevent too frequent re-renders
      this.#debouncedUpdate();
    });
  }

  /**
   * Updates the visual slider range indicator
   */
  #updateSliderRange() {
    const { minInput, maxInput, sliderRange } = this.refs;
    if (!minInput || !maxInput || !sliderRange) return;

    const minValue = parseInt(minInput.value, 10);
    const maxValue = parseInt(maxInput.value, 10);
    const min = parseInt(minInput.min, 10);
    const max = parseInt(minInput.max, 10);

    const minPercent = ((minValue - min) / (max - min)) * 100;
    const maxPercent = ((maxValue - min) / (max - min)) * 100;

    sliderRange.style.left = `${minPercent}%`;
    sliderRange.style.width = `${maxPercent - minPercent}%`;
  }

  /**
   * Updates the price display values
   */
  #updateDisplay() {
    const { minInput, maxInput, minDisplay, maxDisplay } = this.refs;
    if (!minInput || !maxInput) return;

    const minValue = parseInt(minInput.value, 10) / 100;
    const maxValue = parseInt(maxInput.value, 10) / 100;

    if (minDisplay) {
      minDisplay.textContent = minValue.toFixed(2);
    }
    if (maxDisplay) {
      maxDisplay.textContent = maxValue.toFixed(2);
    }
  }

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;
    if (!minInput || !maxInput) return;

    // Ensure values are within bounds
    const min = parseInt(minInput.min, 10);
    const max = parseInt(minInput.max, 10);
    let minValue = parseInt(minInput.value, 10);
    let maxValue = parseInt(maxInput.value, 10);

    if (minValue < min) minValue = min;
    if (minValue > max) minValue = max;
    if (maxValue < min) maxValue = min;
    if (maxValue > max) maxValue = max;
    if (minValue > maxValue) minValue = maxValue;

    minInput.value = minValue.toString();
    maxInput.value = maxValue.toString();

    // Convert cents to dollars for the form values (Shopify expects dollars)
    const minValueDollars = (minValue / 100).toFixed(2);
    const maxValueDollars = (maxValue / 100).toFixed(2);

    // Update the hidden form inputs with dollar values
    const { minHiddenInput, maxHiddenInput } = this.refs;
    if (minHiddenInput instanceof HTMLInputElement) {
      minHiddenInput.value = minValueDollars;
    }
    if (maxHiddenInput instanceof HTMLInputElement) {
      maxHiddenInput.value = maxValueDollars;
    }

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSummary();
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    if (!minInput || !maxInput) return;

    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    // Create temporary input elements for the summary update
    const tempMinInput = document.createElement('input');
    tempMinInput.value = (parseInt(minInput.value, 10) / 100).toFixed(2);
    const tempMaxInput = document.createElement('input');
    tempMaxInput.value = (parseInt(maxInput.value, 10) / 100).toFixed(2);

    statusComponent?.updatePriceSummary(tempMinInput, tempMaxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    let url = this.dataset.url;
    if (!url) return;

    // CRITICAL: For variant display, preserve sort_by=manual when clearing filters
    const isVariantListing = document.documentElement.dataset.variantListing === 'true';
    if (isVariantListing) {
      try {
        const urlObj = new URL(url, window.location.origin);
        // If sort_by is not in the URL, add it as manual
        if (!urlObj.searchParams.has('sort_by')) {
          urlObj.searchParams.set('sort_by', 'manual');
          url = urlObj.pathname + urlObj.search + urlObj.hash;
        }
      } catch (e) {
        // If URL parsing fails, try to append sort_by manually
        if (!url.includes('sort_by=')) {
          const separator = url.includes('?') ? '&' : '?';
          url = url + separator + 'sort_by=manual';
        }
      }
    }

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = '0') {
    const parts = value ? value.trim().split(/[^0-9]/) : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fractionStr, ...rest] = parts;
    if (typeof wholeStr !== 'string' || rest.length > 0) return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || '0', 10);

    // Use two most-significant digits, e.g. 1 -> 10, 12 -> 12, 123 -> 12.3, 1234 -> 12.34, etc
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};
/**
 * Update active state on filter buttons based on active filters
 * For color filters (checkbox swatches), ensure every selected color is active.
 * @param {Object} activeFilters - Active filter values
 */
const updateFilterButtonActiveStates = (activeFilters) => {
  if (!activeFilters) return;
  
  // Find all color filter swatches
  const allColorSwatches = document.querySelectorAll('.facets__item .color-swatch[data-color]');
  
  if (activeFilters.colors.length > 0) {
    const activeColors = activeFilters.colors;

    allColorSwatches.forEach((swatch) => {
      if (!(swatch instanceof HTMLElement)) return;

      const input = swatch.querySelector('input[type="checkbox"], input[type="radio"]');
      const swatchColor = swatch.dataset.color || '';
      const filterValue =
        swatch.dataset.filterValue ||
        (input instanceof HTMLInputElement ? input.value : '') ||
        '';

      const matchesAny = activeColors.some((activeColor) => {
        if (!activeColor) return false;
        return (
          swatchColor === activeColor ||
          filterValue === activeColor ||
          normalizeString(swatchColor) === normalizeString(activeColor) ||
          (filterValue && normalizeString(filterValue) === normalizeString(activeColor)) ||
          normalizeString(swatchColor).includes(normalizeString(activeColor)) ||
          normalizeString(activeColor).includes(normalizeString(swatchColor))
        );
      });

      swatch.classList.toggle('active', matchesAny);
      if (matchesAny) {
        swatch.setAttribute('data-active', 'true');
      } else {
        swatch.removeAttribute('data-active');
      }

      if (input instanceof HTMLInputElement) {
        input.checked = matchesAny;
      }
    });
  } else {
    // No active filters - remove active state from all color filters
    // When filters are cleared, check ALL product cards (not filtered) to determine available colors
    const allProductCards = Array.from(document.querySelectorAll('.product-grid__item[data-variant-color]'));
    const availableColors = new Set();
    allProductCards.forEach((card) => {
      if (card instanceof HTMLElement) {
        const variantColor = card.getAttribute('data-variant-color');
        if (variantColor) {
          availableColors.add(normalizeString(variantColor));
        }
      }
    });
    
    allColorSwatches.forEach((swatch) => {
      if (!(swatch instanceof HTMLElement)) return;
      swatch.classList.remove('active');
      swatch.removeAttribute('data-active');
      const input = swatch.querySelector('input[type="checkbox"], input[type="radio"]');
      if (input instanceof HTMLInputElement) {
        input.checked = false;
      }
      
      // When filters are cleared, update data-option-available based on all products
      const swatchColor = swatch.dataset.color || '';
      const normalizedColor = normalizeString(swatchColor);
      const hasAvailableProducts = availableColors.has(normalizedColor) || 
                                   Array.from(availableColors).some(color => 
                                     color.includes(normalizedColor) || normalizedColor.includes(color)
                                   );
      
      // Update data-option-available - remove disabled state when filters are cleared
      if (hasAvailableProducts || allProductCards.length === 0) {
        // If there are products with this color, or no products at all, mark as available
        swatch.setAttribute('data-option-available', 'true');
      } else {
        swatch.setAttribute('data-option-available', 'false');
      }
    });
  }
};

/**
 * Sync all filter active states with URL parameters
 * Updates checkboxes, radio buttons, and visual active states for ALL filter types
 * @param {URLSearchParams} urlParams - URL search parameters
 */
const syncAllFilterActiveStates = (urlParams) => {
  if (!urlParams) return;
  
  // Find all filter inputs (checkboxes and radios) in facets
  const allFilterInputs = document.querySelectorAll('.facets__item input[type="checkbox"], .facets__item input[type="radio"]');
  
  allFilterInputs.forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;
    
    const inputName = input.name;
    const inputValue = input.value;
    
    // Check if this filter parameter exists in URL
    const urlValue = urlParams.get(inputName);
    const urlValues = urlParams.getAll(inputName); // Get all values for arrays
    
    // Determine if this input should be checked
    let shouldBeChecked = false;
    
    if (urlValue !== null) {
      // For single values, check exact match
      if (urlValue === inputValue || urlValues.includes(inputValue)) {
        shouldBeChecked = true;
      }
    }
    
    // Update checkbox/radio state
    input.checked = shouldBeChecked;
    
    // Update visual active state
    const label = input.closest('label') || input.parentElement?.querySelector('label[for="' + input.id + '"]');
    const swatch = input.closest('.color-swatch') || label?.closest('.color-swatch');
    const pillLabel = label?.closest('.facets__pill-label');
    const listItem = input.closest('li.facets__inputs-list-item');
    
    // Update swatch active state
    if (swatch instanceof HTMLElement) {
      if (shouldBeChecked) {
        swatch.classList.add('active');
        swatch.setAttribute('data-active', 'true');
      } else {
        swatch.classList.remove('active');
        swatch.removeAttribute('data-active');
      }
    }
    
    // Update pill label active state
    if (pillLabel instanceof HTMLElement) {
      if (shouldBeChecked) {
        pillLabel.classList.add('active');
      } else {
        pillLabel.classList.remove('active');
      }
    }
    
    // Update list item active state
    if (listItem instanceof HTMLElement) {
      if (shouldBeChecked) {
        listItem.classList.add('active');
      } else {
        listItem.classList.remove('active');
      }
    }
  });
};

// Listen for filter updates - but SKIP if filtering was already done by runReinit
// This prevents double-filtering after AJAX updates
document.addEventListener(ThemeEvents.FilterUpdate, (event) => {
  if (!(event instanceof FilterUpdateEvent)) return;
  
  // Check if this came from runReinit (which already filtered)
  // We can detect this by checking if facets:update was just dispatched
  // For now, just skip filtering here - runReinit handles it all
  
  // IMPORTANT:
  // `event.detail.queryParams` can be stale in cases where filters are updated by URL (e.g. "Clear all"),
  // because the form state may not yet reflect the new URL at dispatch time.
  // Use the current URL as the source of truth so active states always clear correctly.
  const activeFilters = extractActiveFilters(new URLSearchParams(window.location.search));
  const urlParams = new URLSearchParams(window.location.search);
  
  // Update active states for all filter types (colors, price, product type, etc.)
  updateFilterButtonActiveStates(activeFilters);
  syncAllFilterActiveStates(urlParams);
  
  // Update color filter availability after other filters change
  updateColorFilterAvailabilityAfterFilters();
  
  // Call applyColorFilter for featured variant gender filtering
  // This ensures gender filters work on mobile when Men/Women buttons are clicked
  const globalWindow = /** @type {any} */ (window);
  if (typeof globalWindow.applyColorFilter === 'function') {
    // Apply immediately for instant feedback
    globalWindow.applyColorFilter();
    // Also apply after URL updates
    setTimeout(() => {
      globalWindow.applyColorFilter();
    }, 50);
  }
});

// Apply filters on page load/refresh by reading URL parameters
const applyFiltersOnPageLoad = () => {
  const urlParams = new URLSearchParams(window.location.search);

  // If the URL has a page param > 1 AND active filters AND the server rendered
  // no products (empty grid / Liquid "no products found"), the page param likely
  // exceeds the available pages for the filtered results in this market.
  // Redirect to the same URL without the page param so the user sees page 1.
  const pageParam = parseInt(urlParams.get('page') || '0', 10);
  if (pageParam > 1) {
    const hasFilterParam = Array.from(urlParams.keys()).some(k => k.startsWith('filter.'));
    if (hasFilterParam) {
      const grid = document.querySelector('.product-grid, ul.product-grid');
      const gridItems = grid ? grid.querySelectorAll('.product-grid__item, li.product-grid__item') : [];
      const emptyMsg = document.querySelector('.main-collection-grid__empty');
      if (gridItems.length === 0 || (emptyMsg instanceof HTMLElement && emptyMsg.offsetParent !== null)) {
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('page');
        window.location.replace(cleanUrl.toString());
        return;
      }
    }
  }

  const activeFilters = extractActiveFilters(urlParams);
  
  const applyFiltersOnce = () => {
    // Update active state on filter buttons
    updateFilterButtonActiveStates(activeFilters);
    
    if (activeFilters.colors.length > 0 || activeFilters._serverFiltered) {
      // Apply filters (or just swap images when _serverFiltered)
      applyFiltersToProductCards(activeFilters, true);
      // Hide unavailable filters only when filters are active
      if (activeFilters.colors.length > 0) {
        requestAnimationFrame(() => {
          hideUnavailableColorFilters();
        });
      }
      // Re-apply filter images after other init scripts (requestIdleCallback, timeouts)
      // have had a chance to run, preventing them from reverting to the default image.
      setTimeout(() => {
        applyFilterImagesToCards(activeFilters);
      }, 2500);
    } else {
      // No filters active - ensure all filters are visible
      const allFilterSwatches = document.querySelectorAll('.facets__item .color-swatch');
      allFilterSwatches.forEach((swatch) => {
        if (swatch instanceof HTMLElement) {
          const listItem = swatch.closest('li.facets__inputs-list-item');
          const targetElement = listItem instanceof HTMLElement ? listItem : swatch;
          targetElement.style.display = '';
          targetElement.removeAttribute('data-no-products');
          targetElement.classList.remove('out-of-stock');
        }
      });
    }
    
    // Always ensure all filters are visible - never hide any
    hideUnavailableColorFilters();
    
    // Call applyColorFilter for featured variant gender filtering
    // Check if there are product type/gender filters in the URL
    const urlParams = new URLSearchParams(window.location.search);
    let hasProductTypeFilter = false;
    for (const [key] of urlParams.entries()) {
      const filterKeyLower = key.toLowerCase();
      if (filterKeyLower.startsWith('filter.p.t.') || 
          filterKeyLower.startsWith('filter.p.product_type') ||
          (filterKeyLower.startsWith('filter.') && filterKeyLower.includes('product_type')) ||
          (filterKeyLower.includes('gender'))) {
        hasProductTypeFilter = true;
        break;
      }
    }
    
    if (hasProductTypeFilter) {
      const globalWindow = /** @type {any} */ (window);
      if (typeof globalWindow.applyColorFilter === 'function') {
        // Delay to ensure DOM is ready
        setTimeout(() => {
          globalWindow.applyColorFilter();
        }, 100);
      }
    }
  };
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyFiltersOnce, { once: true });
  } else {
    // Use requestAnimationFrame to ensure DOM is fully rendered
    requestAnimationFrame(applyFiltersOnce);
  }
};

// Run on page load
applyFiltersOnPageLoad();

// Re-apply filters after navigation (back/forward, bfcache restore).
const reapplyFiltersForNavigation = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const activeFilters = extractActiveFilters(urlParams);

  updateFilterButtonActiveStates(activeFilters);

  /** @type {any} */
  const globalWindow = window;
  if (typeof globalWindow.assignColorGroups === 'function') {
    globalWindow.assignColorGroups();
  }
  applyFiltersToProductCards(activeFilters, true);

  // Explicitly re-apply filter images to every product card after navigation.
  // On bfcache restore the DOM may be stale; on popstate the cards need a fresh swap.
  if (activeFilters.colors.length > 0 || activeFilters._serverFiltered) {
    setTimeout(() => {
      const imgFilters = activeFilters.colors.length > 0
        ? activeFilters
        : (() => {
            const domColors = [];
            document.querySelectorAll(
              '.facets__item .color-swatch.active[data-color], .facets__item .color-swatch[data-active="true"][data-color]'
            ).forEach((sw) => {
              if (sw instanceof HTMLElement && sw.dataset.color) domColors.push(sw.dataset.color);
            });
            return domColors.length > 0
              ? { colors: domColors, variants: [], other: {}, colorGroups: {} }
              : null;
          })();
      if (imgFilters) {
        applyFilterImagesToCards(imgFilters);
      }
    }, 150);
  }

  // Only call applyColorFilter when gender/product-type filters are present
  let hasProductTypeFilter = false;
  for (const [key] of urlParams.entries()) {
    const k = key.toLowerCase();
    if (k.startsWith('filter.p.t.') || k.includes('product_type') || k.includes('gender')) {
      hasProductTypeFilter = true;
      break;
    }
  }
  if (hasProductTypeFilter && typeof globalWindow.applyColorFilter === 'function') {
    setTimeout(() => { globalWindow.applyColorFilter(); }, 50);
  }
};

window.addEventListener('popstate', reapplyFiltersForNavigation);

// Handle bfcache restoration (PDP → back to PLP)
window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    reapplyFiltersForNavigation();
  }
});

// Watch for new product cards added by pagination and apply filter images to them.
(function observeNewProductCards() {
  const grid = document.querySelector('.product-grid, ul.product-grid');
  if (!grid) return;

  let debounceTimer = null;
  const observer = new MutationObserver((mutations) => {
    // Only act when a color filter is in the URL
    const search = window.location.search;
    if (search.indexOf('filter.v.option.color') === -1 && search.indexOf('filter.v.option.colour') === -1) return;

    let hasNewCards = false;
    for (const m of mutations) {
      if (!m.addedNodes || !m.addedNodes.length) continue;
      for (const node of m.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        if (node.classList.contains('sunice-product-card') ||
            node.querySelector('.sunice-product-card') ||
            node.classList.contains('product-grid__item') ||
            node.querySelector('.product-grid__item')) {
          hasNewCards = true;
          break;
        }
      }
      if (hasNewCards) break;
    }
    if (!hasNewCards) return;

    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      const urlParams = new URLSearchParams(window.location.search);
      const activeFilters = extractActiveFilters(urlParams);
      if (activeFilters.colors.length === 0 && !activeFilters._serverFiltered) return;

      const imgFilters = activeFilters.colors.length > 0
        ? activeFilters
        : (() => {
            const domColors = [];
            document.querySelectorAll(
              '.facets__item .color-swatch.active[data-color], .facets__item .color-swatch[data-active="true"][data-color]'
            ).forEach((sw) => {
              if (sw instanceof HTMLElement && sw.dataset.color) domColors.push(sw.dataset.color);
            });
            return domColors.length > 0
              ? { colors: domColors, variants: [], other: {}, colorGroups: {} }
              : null;
          })();
      if (!imgFilters) return;

      applyFilterImagesToCards(imgFilters);
    }, 300);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      const g = document.querySelector('.product-grid, ul.product-grid');
      if (g) observer.observe(g, { childList: true, subtree: true });
    });
  } else {
    observer.observe(grid, { childList: true, subtree: true });
  }
})();

// ENHANCED: Listen for clicks AND change events on color swatches in variant pickers
// This ensures product card images update when clicking color swatches in the variant picker
// CRITICAL: This is for general product card functionality, not just filters
function updateProductCardImagesFromColorSwatch(colorSwatch, radio) {
  if (!(colorSwatch instanceof HTMLElement) || !(radio instanceof HTMLInputElement)) return;
  
  // Get image URLs from the radio button - handle protocol-relative URLs
  let imageUrl = radio.dataset.imageUrl || '';
  let secondaryImageUrl = radio.dataset.secondaryImageUrl || '';
  
  // If radio doesn't have image URLs, try to get from the label
  if (!imageUrl) {
    imageUrl = colorSwatch.dataset.imageUrl || '';
    secondaryImageUrl = colorSwatch.dataset.secondaryImageUrl || secondaryImageUrl;
  }
  
  // Ensure URLs have protocol (handle protocol-relative URLs)
  if (imageUrl && imageUrl.startsWith('//')) {
    imageUrl = window.location.protocol + imageUrl;
  }
  if (secondaryImageUrl && secondaryImageUrl.startsWith('//')) {
    secondaryImageUrl = window.location.protocol + secondaryImageUrl;
  }
  
  if (!imageUrl && !secondaryImageUrl) return;
  
  const picker = colorSwatch.closest('.sunice-variant-picker');
  if (!(picker instanceof HTMLElement)) return;
  
  // Find the product card that contains this variant picker
  // Try multiple methods to find the product card - be very thorough
  let productCard = picker.closest('.sunice-product-card');
  
  // If not found, try to find by product ID (most reliable method)
  if (!productCard) {
    const productId = picker.dataset.productId;
    if (productId) {
      // Try exact match first
      productCard = document.querySelector('.sunice-product-card[data-product-id="' + productId + '"]');
      
      // If still not found, search all product cards
      if (!productCard) {
        const allProductCards = document.querySelectorAll('.sunice-product-card');
        for (const card of allProductCards) {
          if (card instanceof HTMLElement) {
            const cardProductId = card.dataset.productId || 
                                 card.getAttribute('data-product-id') ||
                                 card.querySelector('[data-product-id]')?.getAttribute('data-product-id');
            if (cardProductId === productId || String(cardProductId) === String(productId)) {
              productCard = card;
              break;
            }
          }
        }
      }
    }
  }
  
  // If still not found, try to find in the same container (form or product-form-container)
  if (!productCard) {
    const container = picker.closest('.product-form-container, .product-form, form');
    if (container instanceof HTMLElement) {
      productCard = container.closest('.sunice-product-card');
      
      // Also try finding by traversing up the DOM tree
      if (!productCard) {
        let parent = container.parentElement;
        let depth = 0;
        while (parent && depth < 10) {
          if (parent.classList.contains('sunice-product-card')) {
            productCard = parent;
            break;
          }
          parent = parent.parentElement;
          depth++;
        }
      }
    }
  }
  
  // Last resort: search entire document for product card with matching product ID
  if (!productCard) {
    const productId = picker.dataset.productId;
    if (productId) {
      const allProductCards = document.querySelectorAll('.sunice-product-card');
      for (const card of allProductCards) {
        if (card instanceof HTMLElement) {
          const cardProductId = card.dataset.productId || 
                               card.getAttribute('data-product-id') ||
                               card.querySelector('[data-product-id]')?.getAttribute('data-product-id');
          if (cardProductId === productId || String(cardProductId) === String(productId)) {
            productCard = card;
            break;
          }
        }
      }
    }
  }
  
  if (productCard instanceof HTMLElement) {
    const globalWindow = /** @type {any} */ (window);
    // Update product card images IMMEDIATELY
    if (typeof globalWindow.updateProductCardPrimaryImage === 'function') {
      globalWindow.updateProductCardPrimaryImage(productCard, imageUrl, secondaryImageUrl);
    } else {
      // Fallback: manually update images - try multiple selectors
      const imageContainer = productCard.querySelector('.product-card__image');
      let primaryImg = productCard.querySelector('.product-image-primary');
      let secondaryImg = productCard.querySelector('.product-image-secondary');
      
      // If not found by class, try finding in image container
      if (!primaryImg && imageContainer) {
        primaryImg = imageContainer.querySelector('img.product-image-primary, img:first-child');
      }
      if (!secondaryImg && imageContainer) {
        secondaryImg = imageContainer.querySelector('img.product-image-secondary, img:nth-child(2)');
      }
      
      // Last resort: find first and second images in container
      if (!primaryImg && imageContainer) {
        const allImages = imageContainer.querySelectorAll('img');
        if (allImages.length > 0) {
          primaryImg = allImages[0];
        }
        if (allImages.length > 1) {
          secondaryImg = allImages[1];
        }
      }
      
      // Update primary image
      if (primaryImg instanceof HTMLImageElement && imageUrl) {
        primaryImg.src = imageUrl;
        if (primaryImg.srcset !== undefined) {
          primaryImg.srcset = imageUrl;
        }
        // Force reload
        primaryImg.loading = 'eager';
      }
      
      // Update secondary image
      if (secondaryImg instanceof HTMLImageElement && secondaryImageUrl) {
        secondaryImg.src = secondaryImageUrl;
        if (secondaryImg.srcset !== undefined) {
          secondaryImg.srcset = secondaryImageUrl;
        }
        // Force reload
        secondaryImg.loading = 'eager';
      }
    }
    
    // Also update color swiper swatches to match the selected color
    const colorSwiperContainer = productCard.querySelector('.color-swiper-container');
    if (colorSwiperContainer instanceof HTMLElement) {
      const colorValue = radio.value;
      const colorSwatches = colorSwiperContainer.querySelectorAll('.color-swatch');
      
      // Find and activate the matching color swatch in the color swiper
      for (const swatch of colorSwatches) {
        if (!(swatch instanceof HTMLElement)) continue;
        const swatchColor = swatch.dataset.color || swatch.getAttribute('data-variant-color');
        if (swatchColor) {
          const normalizedSwatchColor = normalizeString(swatchColor);
          const normalizedRadioColor = normalizeString(colorValue);
          
          // Check if colors match
          if (normalizedSwatchColor === normalizedRadioColor ||
              normalizedSwatchColor.includes(normalizedRadioColor) ||
              normalizedRadioColor.includes(normalizedSwatchColor)) {
            // Update active state
            colorSwatches.forEach((s) => {
              if (s instanceof HTMLElement) {
                s.removeAttribute('data-active');
              }
            });
            swatch.setAttribute('data-active', 'true');
            break;
          }
        }
      }
    }

    // Keep PDP links synced to the selected variant during facet interactions.
    const form = picker.closest('form');
    let selectedVariantId = '';
    if (form instanceof HTMLFormElement) {
      const variantIdInput = form.querySelector('input[name="id"]');
      if (variantIdInput instanceof HTMLInputElement && variantIdInput.value) {
        selectedVariantId = variantIdInput.value;
      }
    }
    if (!selectedVariantId) {
      selectedVariantId = radio.dataset.variantId || '';
    }
    if (selectedVariantId) {
      updateProductCardLinksForVariant(productCard, selectedVariantId);
    }
  }
}

// Listen for clicks on color swatches - CRITICAL: Must run early to catch before other handlers
// This handles clicks on color swatches INSIDE the variant picker (not in color-swiper-container)
document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  
  // Check if the click is on a color swatch label or its child elements in a variant picker
  // IMPORTANT: This is for .sunice-variant-picker .color-swatch (inside the form)
  // NOT for .color-swiper-container .color-swatch (which is handled by sunice-product-card.js)
  const colorSwatch = target.closest('.sunice-variant-picker .color-swatch');
  if (!(colorSwatch instanceof HTMLElement)) return;
  
  // Find the radio button for this color swatch
  const radio = colorSwatch.querySelector('input[type="radio"]');
  if (!(radio instanceof HTMLInputElement)) return;
  
  // Ensure radio is checked (label click should check it, but ensure it's checked)
  if (!radio.checked) {
    radio.checked = true;
  }
  
  // Update images IMMEDIATELY - no delays, run synchronously
  updateProductCardImagesFromColorSwatch(colorSwatch, radio);
}, true); // Use capture phase to catch clicks early - runs BEFORE other handlers

// ALSO listen for change events on radio buttons (backup for programmatic changes)
document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
  
  // Check if this radio is inside a color swatch in a variant picker
  const colorSwatch = target.closest('.sunice-variant-picker .color-swatch');
  if (!(colorSwatch instanceof HTMLElement)) return;
  
  // Only update if this radio is now checked
  if (!target.checked) return;
  
  // Update images immediately - no delays
  updateProductCardImagesFromColorSwatch(colorSwatch, target);
}, true); // Use capture phase

// ALSO listen for direct clicks on radio buttons inside color swatches
document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
  
  // Check if this radio is inside a color swatch in a variant picker
  const colorSwatch = target.closest('.sunice-variant-picker .color-swatch');
  if (!(colorSwatch instanceof HTMLElement)) return;
  
  // Update images immediately when radio is clicked
  // Use setTimeout(0) to ensure radio.checked is updated first
  setTimeout(() => {
    if (target.checked) {
      updateProductCardImagesFromColorSwatch(colorSwatch, target);
    }
  }, 0);
}, true); // Use capture phase

// DISABLED: This setTimeout was causing variants to show then disappear
// Filtering is already handled by runReinit() after AJAX updates
// setTimeout(() => {
//   const urlParams = new URLSearchParams(window.location.search);
//   const activeFilters = extractActiveFilters(urlParams);
//   if (activeFilters.colors.length > 0) {
//     applyFiltersToProductCards(activeFilters);
//   }
// }, 500);

/**
 * CUSTOM VARIANT CARD FILTERING
 * Simple, independent filtering system specifically for variant product cards
 * This bypasses the complex facet system and uses direct DOM manipulation
 */
const filterVariantCards = (filterValue) => {
  
  // Check if variant listing is enabled
  const variantGridItems = document.querySelectorAll('.product-grid__item[data-variant-color]');
  
  if (variantGridItems.length === 0) {
    // Not variant listing mode, skip
    return;
  }
  
  if (!filterValue || filterValue.trim() === '') {
    // No filter - show all variant cards (respecting pagination)
    variantGridItems.forEach((gridItem) => {
      if (gridItem instanceof HTMLElement) {
        gridItem.classList.remove('filter-hidden');
        // Restore pagination state
        const page = parseInt(gridItem.dataset.page || '1', 10);
        if (page === 1) {
          gridItem.classList.remove('product-grid__item--hidden');
          gridItem.classList.add('active');
        } else {
          gridItem.classList.add('product-grid__item--hidden');
          gridItem.classList.remove('active');
        }
        gridItem.style.display = '';
      }
    });
    return;
  }
  
  // Filter active - show/hide variant cards based on title match
  // First, get all colors in the filter group (e.g., "Red" group might contain ["Red", "Scarlet", "Crimson"])
  const filterGroupColors = getFilterGroupColors(filterValue);
  
  // Normalize all filter group colors for comparison
  const normalizedFilterGroup = filterGroupColors.map(color => normalizeString(color));
  const normalizedFilter = normalizeString(filterValue);
  let visibleCount = 0;
  
  // Remove pagination hidden class from all variant cards so we can filter them
  variantGridItems.forEach((gridItem) => {
    if (gridItem instanceof HTMLElement) {
      gridItem.classList.remove('product-grid__item--hidden');
    }
  });
  
  variantGridItems.forEach((gridItem) => {
    if (!(gridItem instanceof HTMLElement)) return;
    
    // Get variant title from the displayed text
    const variantTitleElement = gridItem.querySelector('.variant-title');
    const variantTitle = variantTitleElement ? (variantTitleElement.textContent || '').trim() : '';
    
    // Fallback to data-variant-color if variant title not found
    const variantColor = variantTitle || gridItem.dataset.variantColor || '';
    
    if (!variantColor) {
      // No variant title or color - hide it
      gridItem.classList.add('filter-hidden');
      gridItem.style.display = 'none';
      return;
    }
    
    // Normalize variant title for comparison
    const normalizedTitle = normalizeString(variantColor);
    let matches = false;
    
    // Check if variant title matches ANY color in the filter group
    for (const groupColor of normalizedFilterGroup) {
      // Direct match: variant contains group color or group color contains variant
      if (normalizedTitle.includes(groupColor) || groupColor.includes(normalizedTitle)) {
        matches = true;
        break;
      }
      
      // Word-by-word matching for partial matches (e.g., "Orange" matches "Orange/White")
      const titleWords = normalizedTitle.split(/[\s\/\\\-_]+/);
      const groupColorWords = groupColor.split(/[\s\/\\\-_]+/);
      
      for (const groupWord of groupColorWords) {
        if (titleWords.some(titleWord => 
          titleWord === groupWord || 
          titleWord.includes(groupWord) || 
          groupWord.includes(titleWord)
        )) {
          matches = true;
          break;
        }
      }
      
      if (matches) break;
    }
    
    // Also check against the original filter value as a fallback
    if (!matches) {
      if (normalizedTitle.includes(normalizedFilter) || normalizedFilter.includes(normalizedTitle)) {
        matches = true;
      } else {
        // Word-by-word fallback
        const titleWords = normalizedTitle.split(/[\s\/\\\-_]+/);
        const filterWords = normalizedFilter.split(/[\s\/\\\-_]+/);
        
        for (const filterWord of filterWords) {
          if (titleWords.some(titleWord => 
            titleWord === filterWord || 
            titleWord.includes(filterWord) || 
            filterWord.includes(titleWord)
          )) {
            matches = true;
            break;
          }
        }
      }
    }
    
    if (matches) {
      const isAvailable = gridItem.dataset.available !== 'false';
      if (isAvailable) {
        gridItem.classList.remove('filter-hidden');
        gridItem.style.display = '';
        visibleCount++;
      } else {
        gridItem.classList.add('filter-hidden');
        gridItem.style.display = 'none';
      }
    } else {
      // Hide this variant card - its title doesn't match the filter or any color in the group
      gridItem.classList.add('filter-hidden');
      gridItem.style.display = 'none';
    }
  });
  
  
  // Show/hide "no products" message
  const productGrid = document.querySelector('.product-grid, ul.product-grid');
  const existingEmptyMessage = document.querySelector('.main-collection-grid__empty, .filter-no-products-message');
  
  if (visibleCount === 0) {
    if (productGrid instanceof HTMLElement) {
      productGrid.style.display = 'none';
    }
    
    if (existingEmptyMessage instanceof HTMLElement) {
      existingEmptyMessage.style.display = '';
    } else {
      const emptyMessage = document.createElement('div');
      emptyMessage.className = 'main-collection-grid__empty filter-no-products-message';
      emptyMessage.innerHTML = `
        <h2 class="main-collection-grid__empty-title h2">No products available</h2>
        <p>There are no products available for the selected filter. <a href="${window.location.pathname}" class="main-collection-grid__empty-link">Clear filters</a></p>
      `;
      
      const resultsContainer = productGrid?.parentElement || document.querySelector('.collection-wrapper, results-list');
      if (resultsContainer instanceof HTMLElement) {
        resultsContainer.insertBefore(emptyMessage, productGrid);
      } else if (productGrid instanceof HTMLElement) {
        productGrid.parentElement?.insertBefore(emptyMessage, productGrid);
      }
    }
  } else {
    if (productGrid instanceof HTMLElement) {
      productGrid.style.display = '';
    }
    
    if (existingEmptyMessage instanceof HTMLElement) {
      existingEmptyMessage.style.display = 'none';
    }
  }
};

// DISABLED: Click handler for filterVariantCards is now handled by the main facet system
// The FacetsFormComponent handles filter clicks and triggers applyFiltersToProductCards,
// which already detects variant listing mode and uses VariantListingFilter correctly.
// This prevents double filtering conflicts.
//
// document.addEventListener('click', (event) => {
//   ...
//   filterVariantCards(finalFilterValue);
// }, true);

// DISABLED: filterVariantCards is now handled by applyFiltersToProductCards via VariantListingFilter
// This prevents double filtering conflicts. The main filtering system (applyFiltersToProductCards)
// already detects variant listing mode and calls VariantListingFilter.applyFilters() correctly.
// 
// document.addEventListener(ThemeEvents.FilterUpdate, (event) => {
//   if (!(event instanceof FilterUpdateEvent)) return;
//   ...
// });

// DISABLED: applyVariantCardFilterOnLoad is now handled by the main filtering system
// The main filter initialization (in FacetsFormComponent and applyFiltersToProductCards)
// already handles variant listing mode correctly via VariantListingFilter.
// This prevents double filtering conflicts on page load.
//
// const applyVariantCardFilterOnLoad = () => {
//   ...
// };
//
// if (document.readyState === 'loading') {
//   ...
// }


